<template>
  <div
    ref="mapContainer"
    class="overview-map-container"
    :class="{ 'has-targeting-summary': enableTargetingSummary && isViewSummaryVisible }">
    <map-targeting-summary
      v-if="enableTargetingSummary"
      :cart="cart"
      :params="params"
      @on-change="
        (val) => {
          isViewSummaryVisible = val;
          $emit('showViewSummary', val);
        }
      " />
    <div class="flex-v-full">
      <!-- FILTERS AND MAP -->
      <div
        class="filters-and-map"
        style="width: 100%; flex-grow: 1; position: relative">
        <div
          v-if="organizationSettings?.uiEnableSelectTargetingToSeeScreens && !hasStartedFiltering"
          class="message-select-targeting">
          <span>{{ capitalize($t('messages.inventoryWillBecomeVisibleAsYouStartFiltering')) }}</span>
        </div>
        <div
          v-if="isReferenceDataReady && showDropDownFilters"
          class="drop-down-filters-container"
          :class="{ visible: activeSite == null && activeUnit == null }">
          <drop-down-group
            v-show="filteredMediaTypes.length > 0 && selectedFilters.find((x) => x.id === 'dd_media_types') != null"
            v-if="areMediaTypesDataReady && selectedFilters.find((x) => x.id === 'dd_media_types') != null"
            id="dd_media_types"
            ref="dd_media_types"
            :allow-select-parent-only="allowVenueTypeSelectParentOnly"
            :allow-exclusions="
              isCuratorOrganization ||
              (lineItem != null && lineItem.type !== 'Package') ||
              creative != null ||
              unitPack != null
            "
            :label="capitalize($t('names.venueType', { n: 2 }))"
            :options="filteredMediaTypes"
            :option-label-property="'formattedName'"
            :option-label-parent-property="'formattedName'"
            :selected-options="cart.mediaTypesInCart"
            :excluded-options="cart.excludedMediaTypes"
            :is-read-only="isReadOnly || areMediaTypesReadOnly"
            :select-all-by-default="!organizationSettings?.uiEnableSelectTargetingToSeeScreens"
            @cleared="onMediaTypeSelectionsCleared"
            @selection-changed="onMediaTypeSelectionChanged"
            @drop-down-visible="() => onFilterDropDownVisible('Venue types')"
            @drop-down-hidden="onFilterDropDownHidden" />
          <drop-down
            v-show="selectedFilters.find((x) => x.id === 'dd_markets') != null"
            v-if="
              selectedFilters.find((x) => x.id === 'dd_markets') != null ||
              (allMarkets != null && allMarkets.length > 0)
            "
            id="dd_markets"
            ref="dd_markets"
            :allow-exclusions="lineItem != null || creative != null || unitPack != null"
            :label="capitalize($t('names.market', { n: 2 }))"
            :options="allMarkets"
            :selected-options="cart.marketsInCart"
            :excluded-options="cart.excludedMarkets"
            :show-floating-label="true"
            :is-read-only="isReadOnly || areMarketsReadOnly"
            :is-loading="allMarkets == null"
            :select-all-by-default="!organizationSettings?.uiEnableSelectTargetingToSeeScreens"
            @cleared="onMarketsSelectionsCleared"
            @selection-changed="onMarketSelectionChanged"
            @drop-down-visible="() => onFilterDropDownVisible('Markets')"
            @drop-down-hidden="onFilterDropDownHidden" />
          <drop-down
            v-show="selectedFilters.find((x) => x.id === 'dd_networks') != null"
            v-if="
              selectedFilters.find((x) => x.id === 'dd_networks') != null ||
              (availableNetworks != null && availableNetworks.length > 0)
            "
            id="dd_networks"
            ref="dd_networks"
            :allow-exclusions="
              (lineItem != null && lineItem.type !== 'Package') || creative != null || unitPack != null
            "
            :label="capitalize($t('names.network', { n: 2 }))"
            :options="availableNetworks"
            :option-label-property="'formattedName'"
            :selected-options="cart.networksInCart"
            :excluded-options="cart.excludedNetworks"
            :show-floating-label="true"
            :select-all-by-default="
              membershipSettings != null &&
              !membershipSettings.uiForceNetworkSelectionInLineitemsAndDeals &&
              !organizationSettings?.uiEnableSelectTargetingToSeeScreens
            "
            :is-mandatory="membershipSettings != null && membershipSettings.uiForceNetworkSelectionInLineitemsAndDeals"
            :return-empty-selected-options-when-all-is-selected="
              membershipSettings != null && !membershipSettings.uiForceNetworkSelectionInLineitemsAndDeals
            "
            :is-read-only="isReadOnly || areNetworksReadOnly"
            :is-loading="availableNetworks == null"
            :mark-as-having-selections-when-all-are-selected="
              membershipSettings != null && membershipSettings.uiForceNetworkSelectionInLineitemsAndDeals
            "
            @cleared="onNetworksSelectionsCleared"
            @selection-changed="onNetworkSelectionChanged"
            @drop-down-visible="() => onFilterDropDownVisible('Networks')"
            @drop-down-hidden="onFilterDropDownHidden" />
          <drop-down
            v-show="selectedFilters.some((x) => x.id === 'dd_proximity')"
            id="dd_proximity"
            ref="dd_proximity"
            :allow-exclusions="true"
            :label="capitalize($t('names.proximity', { n: 1 }))"
            :options="geofenceGroups"
            :option-label-property="'name'"
            :select-all-by-default="false"
            :selected-options="cart.geofenceGroupsInCart"
            :excluded-options="cart.excludedGeofenceGroups"
            :show-select-all="false"
            :return-empty-selected-options-when-all-is-selected="false"
            :mark-as-having-selections-when-all-are-selected="false"
            :show-selections-in-label="true"
            :show-floating-label="true"
            :is-loading="geofenceGroups == null && !Array.isArray(geofenceGroups)"
            :is-read-only="isReadOnly || areProximityGeofenceGroupsReadOnly"
            :allow-duplicate="true"
            :style="`opacity: ${proximityDropDownOpacity};`"
            @cleared="onGeofenceGroupSelectionsCleared"
            @selection-changed="onGeofenceGroupSelectionChanged"
            @drop-down-visible="() => onFilterDropDownVisible('Proximity')"
            @drop-down-hidden="onFilterDropDownHidden"
            @duplicate-selection="(option) => openProximitySearchModal(option.id)">
            <template #drop-down-popup-bottom>
              <div>
                <div
                  v-show="!(isReadOnly || areProximityGeofenceGroupsReadOnly)"
                  class="create-proximity-geofence-button-container">
                  <button
                    class="btn btn-primary btn-xs"
                    @click="() => openProximitySearchModal(null)">
                    <icon-add />
                    {{ $t('actions.createNewProximityGeofence') }}
                  </button>
                </div>
              </div>
            </template>
          </drop-down>
          <drop-down
            v-show="selectedFilters.find((x) => x.id === 'dd_cities') != null"
            v-if="
              selectedFilters.find((x) => x.id === 'dd_cities') != null || (allCities != null && allCities.length > 0)
            "
            id="dd_cities"
            ref="dd_cities"
            :allow-exclusions="showExcludeOption"
            :label="capitalize($t('names.city', { n: 2 }))"
            :options="allCities"
            option-label-property="hierarchyNoCountry"
            :selected-options="cart.citiesInCart"
            :excluded-options="cart.excludedCities"
            :show-floating-label="true"
            :is-read-only="isReadOnly || areLocationsReadOnly || areCitiesReadOnly"
            :is-loading="allCities == null"
            :select-all-by-default="!organizationSettings?.uiEnableSelectTargetingToSeeScreens"
            @cleared="onCitySelectionCleared"
            @selection-changed="onCitySelectionChanged"
            @drop-down-visible="() => onFilterDropDownVisible('Cities')"
            @drop-down-hidden="onFilterDropDownHidden" />
          <drop-down
            v-if="showUnitsDropDownFilter"
            v-show="selectedFilters.find((x) => x.id === 'dd_units') != null || cart.unitsInCart.length > 0"
            id="dd_units"
            ref="dd_units"
            :key="`units_${index}`"
            :allow-exclusions="showExcludeOption"
            :label="capitalize($t('names.unit', { n: 2 }))"
            :options="unitsForFilters"
            :total-options-count="unitsForFiltersTotalCount"
            option-label-property="displayName"
            :search-label-properties="['displayName', 'externalID']"
            :selected-options="cart.unitsInCart"
            :excluded-options="cart.excludedUnits"
            :show-floating-label="true"
            :is-read-only="isReadOnly || areUnitsReadOnly"
            :is-auto-complete="true"
            :is-loading="
              selectedFilters.find((x) => x.id === 'dd_units') != null &&
              selectedFilters.find((x) => x.id === 'dd_units').isLoading
            "
            :is-loading-more-options="isLoadingMoreUnits"
            :search-placeholder="capitalize($t('actions.searchScreens'))"
            :use-checkboxes="false"
            :select-all-by-default="false"
            :show-options-on-empty-search="true"
            :clear-internal-options-on-read-only="isLineItemTypePackage"
            @cleared="onUnitsSelectionsCleared"
            @load-more-options="onUnitsDropDownLoadMoreOptions"
            @search-term-changed="onUnitsDropDownSearchChanged"
            @selection-changed="onUnitSelectionChanged"
            @drop-down-visible="() => onFilterDropDownVisible('Screens')"
            @drop-down-hidden="onFilterDropDownHidden('Screens')">
            <template #drop-down-popup-top>
              <div
                v-if="!areUnitsReadOnly"
                class="drop-down-popup-options ml-4 mb-4">
                <button
                  v-tooltip.top="capitalize($t('actions.uploadScreensFile'))"
                  name="uploadScreens"
                  class="btn btn-default btn-xs btn-rounded"
                  @click="showScreensCSVUploadModal">
                  <icon-upload />
                  {{ $t('messages.uploadAListOfScreenIDsOrUnitIDs') }}
                </button>
              </div>
            </template>
            <template
              v-if="showNoUnitsFoundMsg"
              #drop-down-popup-search-results>
              <div class="ml-5 mb-5">
                <span> {{ $t('messages.noSearchResults') }} </span>
              </div>
            </template>
          </drop-down>
          <drop-down
            v-if="membershipSettings != null && membershipSettings.uiEnableLineItemSiteTargeting"
            v-show="selectedFilters.find((x) => x.id === 'dd_sites') != null"
            id="dd_sites"
            ref="dd_sites"
            :key="`sites_${index}`"
            :allow-exclusions="showExcludeOption"
            :label="capitalize($t('names.site', { n: 2 }))"
            :options="sitesForFilters"
            :total-options-count="sitesForFiltersTotalCount"
            :show-options-on-empty-search="true"
            option-label-property="displayName"
            :search-label-properties="['displayName', 'externalID']"
            :selected-options="cart.sitesInCart"
            :excluded-options="cart.excludedSites"
            :show-floating-label="true"
            :is-read-only="isReadOnly || areSitesReadOnly"
            :is-auto-complete="true"
            :is-loading="
              selectedFilters.find((x) => x.id === 'dd_sites') != null &&
              selectedFilters.find((x) => x.id === 'dd_sites').isLoading
            "
            :is-loading-more-options="isLoadingMoreSites"
            :search-placeholder="capitalize($t('actions.searchSites'))"
            :use-checkboxes="false"
            :select-all-by-default="false"
            :clear-internal-options-on-read-only="isLineItemTypePackage"
            @cleared="onSitesSelectionsCleared"
            @load-more-options="onSitesDropDownLoadMoreOptions"
            @search-term-changed="onSitesDropDownSearchChanged"
            @selection-changed="onSiteSelectionChanged"
            @drop-down-visible="() => onFilterDropDownVisible('Sites')"
            @drop-down-hidden="onFilterDropDownHidden('Sites')">
            <template #drop-down-popup-top>
              <div
                v-if="!areSitesReadOnly"
                class="drop-down-popup-options ml-4 mb-4">
                <button
                  v-tooltip.top="capitalize($t('actions.uploadSitesFile'))"
                  name="uploadSites"
                  class="btn btn-default btn-xs btn-rounded"
                  @click="showSitesCSVUploadModal">
                  <icon-upload />
                  {{ $t('messages.uploadAListOfSiteIDs') }}
                </button>
              </div>
            </template>
            <template
              v-if="showNoSitesFoundMsg"
              #drop-down-popup-search-results>
              <div class="ml-5 mb-5">
                <span> {{ $t('messages.noSearchResults') }} </span>
              </div>
            </template>
          </drop-down>
          <drop-down
            v-show="selectedFilters.find((x) => x.id === 'dd_regions') != null"
            v-if="
              selectedFilters.find((x) => x.id === 'dd_regions') != null ||
              (allRegions != null && allRegions.length > 0)
            "
            id="dd_regions"
            ref="dd_regions"
            :allow-exclusions="showExcludeOption"
            :label="capitalize($t('names.region', { n: 2 }))"
            :options="allRegions"
            :selected-options="cart.regionsInCart"
            :excluded-options="cart.excludedRegions"
            :show-floating-label="true"
            :is-read-only="isReadOnly || areLocationsReadOnly"
            :is-loading="allRegions == null"
            :select-all-by-default="!organizationSettings?.uiEnableSelectTargetingToSeeScreens"
            @cleared="onRegionsSelectionsCleared"
            @selection-changed="onRegionSelectionChanged"
            @drop-down-visible="() => onFilterDropDownVisible('Regions')"
            @drop-down-hidden="onFilterDropDownHidden" />
          <drop-down
            v-show="selectedFilters.find((x) => x.id === OverallMapDropdownFilter.Languages) != null"
            v-if="
              selectedFilters.find((x) => x.id === OverallMapDropdownFilter.Languages) != null || languages?.length > 0
            "
            :id="OverallMapDropdownFilter.Languages"
            :ref="OverallMapDropdownFilter.Languages"
            :allow-exclusions="showExcludeOption"
            :label="capitalize($t('names.language', { n: 2 }))"
            :options="availableLanguages"
            :selected-options="cart.languagesInCart"
            :excluded-options="cart.excludedLanguages"
            :show-floating-label="true"
            :is-read-only="isReadOnly"
            :is-loading="languages == null"
            :show-floating-label-when-only-one-option-is-available="languages?.length === 1"
            :select-all-by-default="false"
            @cleared="onLanguagesSelectionsCleared"
            @selection-changed="onLanguagesSelectionChanged"
            @drop-down-visible="() => onFilterDropDownVisible('Languages')"
            @drop-down-hidden="onFilterDropDownHidden" />
          <drop-down
            v-show="selectedFilters.find((x) => x.id === 'dd_countries') != null"
            v-if="selectedFilters.find((x) => x.id === 'dd_countries') != null || allCountries?.length > 0"
            id="dd_countries"
            ref="dd_countries"
            :allow-exclusions="showExcludeOption"
            :label="capitalize($t('names.country', { n: 2 }))"
            :options="allCountries"
            :selected-options="cart.countriesInCart"
            :excluded-options="cart.excludedCountries"
            :show-floating-label="true"
            :is-read-only="isReadOnly || areLocationsReadOnly"
            :is-loading="allCountries == null"
            :show-floating-label-when-only-one-option-is-available="allCountries?.length === 1"
            :select-all-by-default="!organizationSettings?.uiEnableSelectTargetingToSeeScreens"
            @cleared="onCountriesSelectionsCleared"
            @selection-changed="onCountriesSelectionChanged"
            @drop-down-visible="() => onFilterDropDownVisible('Countries')"
            @drop-down-hidden="onFilterDropDownHidden" />
          <drop-down
            v-show="selectedFilters.find((x) => x.id === 'dd_tags') != null"
            v-if="allTags != null && allTags.length > 0"
            id="dd_tags"
            ref="dd_tags"
            :allow-exclusions="showExcludeOption"
            :label="capitalize($t('names.tag', { n: 2 }))"
            :selected-options="cart.tagsInCart"
            :excluded-options="cart.excludedTags"
            :return-empty-selected-options-when-all-is-selected="false"
            :select-all-by-default="false"
            :options="allTags"
            :show-floating-label="true"
            :show-tooltip-on-options="true"
            :is-read-only="isReadOnly || areTagsReadOnly"
            :show-floating-label-when-only-one-option-is-available="allTags.length === 1"
            @cleared="onTagsSelectionsCleared"
            @selection-changed="onTagsSelectionChanged"
            @drop-down-visible="() => onFilterDropDownVisible('Tags')"
            @drop-down-hidden="onFilterDropDownHidden" />
          <drop-down
            v-if="canShowUnitPacks"
            v-show="selectedFilters.find((x) => x.id === 'dd_unitpacks') != null && !unitPack"
            id="dd_unitpacks"
            ref="dd_unitpacks"
            :key="`unitpacks_${index}`"
            :total-options-count="unitPacksTotalCount"
            :allow-exclusions="lineItem != null || creative != null"
            :label="capitalize($t('names.unitpacks', { n: 2 }))"
            :options="unitPacksForFilters"
            :search-label-properties="['name']"
            :selected-options="cart.unitPacksInCart"
            :excluded-options="cart.excludedUnitPacks"
            :show-floating-label="true"
            :is-read-only="isReadOnly"
            :is-auto-complete="true"
            :search-placeholder="capitalize($t('actions.searchScreenPacks'))"
            :use-checkboxes="false"
            :select-all-by-default="false"
            :clear-internal-options-on-read-only="isLineItemTypePackage"
            :is-loading="
              selectedFilters.find((x) => x.id === 'dd_unitpacks') != null &&
              selectedFilters.find((x) => x.id === 'dd_unitpacks').isLoading &&
              availableUnitPackIDs.length > 0
            "
            :show-options-on-empty-search="true"
            @cleared="onUnitPacksSelectionsCleared"
            @search-term-changed="onUnitPacksDropDownSearchChanged"
            @selection-changed="onUnitPackSelectionChanged"
            @drop-down-visible="onUnitPacksFilterDropDownVisible"
            @drop-down-hidden="onFilterDropDownHidden">
            <template
              v-if="showNoUnitPacksFoundMsg && availableUnitPackIDs.length > 0"
              #drop-down-popup-search-results>
              <div class="ml-5 mb-5">
                <span> {{ $t('messages.noSearchResults') }} </span>
              </div>
            </template>
            <template
              v-else-if="!availableUnitPackIDs.length > 0 && canShowUnitPacks"
              #drop-down-popup-search-results>
              <div class="ml-5 mb-5">
                <span> {{ $t('messages.noAvailableUnitpacks') }} </span>
              </div>
            </template>
          </drop-down>
          <drop-down
            v-show="selectedFilters.find((x) => x.id === 'dd_creative_formats') != null"
            v-if="allCreativeFormats != null && allCreativeFormats.length > 0"
            id="dd_creative_formats"
            ref="dd_creative_formats"
            :allow-exclusions="showExcludeOption"
            :label="capitalize($t('names.creativeFormat', { n: 2 }))"
            :short-label="capitalize($t('names.format', { n: 2 }))"
            :selected-options="cart.creativeFormatsInCart"
            :excluded-options="cart.excludedCreativeFormats"
            :options="allCreativeFormats"
            :is-read-only="isReadOnly || areCreativeFormatsReadOnly"
            :show-floating-label="true"
            :select-all-by-default="!organizationSettings?.uiEnableSelectTargetingToSeeScreens"
            @cleared="onCreativeFormatsSelectionsCleared"
            @selection-changed="onCreativeFormatsSelectionChanged"
            @drop-down-visible="() => onFilterDropDownVisible('Creative formats')"
            @drop-down-hidden="onFilterDropDownHidden" />
          <drop-down
            v-show="selectedFilters.find((x) => x.id === OverallMapDropdownFilter.CreativeOrientations) != null"
            v-if="allCreativeOrientations != null && allCreativeOrientations.length > 0"
            :id="OverallMapDropdownFilter.CreativeOrientations"
            :ref="OverallMapDropdownFilter.CreativeOrientations"
            :allow-exclusions="showExcludeOption"
            :label="capitalize($t('names.creativeOrientation', { n: 2 }))"
            :short-label="capitalize($t('names.orientation', { n: 2 }))"
            :selected-options="cart.creativeOrientationsInCart"
            :excluded-options="cart.excludedCreativeOrientations"
            :options="allCreativeOrientations"
            :is-read-only="isReadOnly || areCreativeOrientationsReadOnly"
            :show-floating-label="true"
            @cleared="onCreativeOrientationsSelectionsCleared"
            @selection-changed="onCreativeOrientationsSelectionChanged"
            @drop-down-visible="() => onFilterDropDownVisible('Creative orientations')"
            @drop-down-hidden="onFilterDropDownHidden" />
          <drop-down-group
            v-show="isIABFilterEnabled"
            v-if="allIabCategories?.length > 0 && isIABFilterEnabled"
            id="dd_iab_categories"
            ref="dd_iab_categories"
            :allow-exclusions="true"
            :label="capitalize($t('names.iabCategory', { n: 2 }))"
            :options="allIabCategories"
            :option-label-property="'description'"
            :option-label-parent-property="'id'"
            :selected-options="cart.iabCategoriesInCart"
            :excluded-options="cart.excludedIabCategories"
            :is-read-only="isReadOnly || areIabCategoriesReadOnly"
            :select-all-by-default="true"
            :return-parent-when-all-children-selected="true"
            :can-sort-list="false"
            @cleared="onIabCategoriesSelectionsCleared"
            @selection-changed="onIabCategoriesSelectionChanged"
            @drop-down-visible="() => onFilterDropDownVisible('Iab categories')"
            @drop-down-hidden="onFilterDropDownHidden" />
          <drop-down
            v-show="!isReadOnly && network == null"
            id="dd_filters"
            class="filter-selection-dropdown"
            :label="
              selectedFilters.length > 0
                ? `+ ${capitalize(i18n.global.t('messages.moreFilters'))}`
                : `+ ${capitalize(i18n.global.t('messages.addFilters'))}`
            "
            :options="availableFilters"
            :selected-options="selectedFilters"
            :show-arrow="false"
            :popup-label="
              selectedFilters.length > 0
                ? capitalize(i18n.global.t('messages.moreFilters'))
                : capitalize(i18n.global.t('messages.addFilters'))
            "
            :show-summary-of-selections-instead-of-label="false"
            :tint-when-selections="false"
            :show-select-all="false"
            :return-empty-selected-options-when-all-is-selected="false"
            :select-all-by-default="false"
            @selection-changed="updateSelectedFilters"
            @drop-down-visible="() => onFilterDropDownVisible('More filters')"
            @drop-down-hidden="onFilterDropDownHidden" />
        </div>
        <mapbox-map
          v-if="isNotChina"
          ref="mapboxMap"
          :show-networks="showNetworks"
          :unique-network-i-ds="uniqueNetworkIDs"
          :cart="cart"
          :centroid-features="[]"
          :clustered-type="clusteredType"
          :geofence-group-polygon-features="geofenceGroupPolygonFeatures"
          :geofence-group-centroid-features="geofenceGroupCentroidFeatures"
          :fit-bounds-right-padding="fitBoundsRightPadding"
          :geocoded-locations-geo-json="geocodedLocationsGeoJson"
          :is-reference-data-ready="isReferenceDataReady"
          :units="units"
          :market-filters="marketFilters"
          :market-polygon-features="marketPolygonFeatures"
          :location-filters="locationFilters"
          :location-polygon-features="locationPolygonFeatures"
          :geofence-group-filters="geofenceGroupFilters"
          :map-control-position="mapControlPosition"
          :color-per-network-i-d="colorPerNetworkID"
          :active-location-min-distance-from-left="activeLocationMinDistanceFromLeft"
          @fit-bounds-triggered="onFitBoundsTriggered"
          @active-unit-i-ds-changed="onActiveUnitIDsChanged"
          @clear-active-units="clearActiveUnits"
          @request-locations-update="getFilteredLocations"
          @close-map-pane="isActiveUnitsPaneVisible = false" />
        <baidu-map-wrapper
          v-else
          ref="baiduMap"
          :centroid-features="[]"
          :geofence-group-polygon-features="geofenceGroupPolygonFeatures"
          :geofence-group-centroid-features="geofenceGroupCentroidFeatures"
          :fit-bounds-right-padding="fitBoundsRightPadding"
          :geocoded-locations-geo-json="geocodedLocationsGeoJson"
          :is-reference-data-ready="isReferenceDataReady"
          :units="units"
          :market-filters="marketFilters"
          :market-polygon-features="marketPolygonFeatures"
          :location-filters="locationFilters"
          :location-polygon-features="locationPolygonFeatures"
          :geofence-group-filters="geofenceGroupFilters"
          @active-unit-i-ds-changed="onActiveUnitIDsChanged"
          @request-locations-update="getFilteredLocations" />
        <!-- MAP PANES -->
        <div
          ref="mapPanes"
          class="map-panes">
          <!-- LEFT PANES -->
          <div class="map-panes-left">
            <map-panes
              v-if="showNewUI && isActiveUnitsPaneVisible && activeUnit"
              :network-count-by-cluster-per-site="networkCountByClusterPerSite"
              :show-networks="showNetworks"
              :site-image="siteImageUrl"
              :active-unit="activeUnit"
              :active-units="activeUnits"
              :media-owners="mediaOwners"
              :networks="activeNetworks"
              :media-types="mediaTypes"
              :unit-details-pane-style="unitDetailsPaneStyle"
              :active-unit-screen-dimension-style="activeUnitScreenDimensionStyle"
              :clustered-type="clusteredType"
              :active-site="activeSite"
              :active-sites="activeSites"
              :color-per-network-i-d="colorPerNetworkID"
              :entity="lineItem"
              @go-to-previous-card="goToPreviousCard"
              @go-to-next-card="goToNextCard" />
            <div
              v-else-if="!showNewUI && isActiveUnitsPaneVisible"
              class="map-pane location-details-pane">
              <!-- UNIT LIST -->
              <div
                v-if="activeUnit == null && activeUnits != null"
                class="map-pane-content unit-list-pane">
                <div class="units-header">
                  <icon-sad v-if="searchResultUnitCount === 0" />
                  <!--<icon-screen v-else></icon-screen>-->
                  <span v-if="areActiveUnitsFromMapInteraction"
                    >{{ activeUnits.length }} {{ $t('messages.screensAtThisSite') }}</span
                  >
                  <span v-else-if="searchResultUnitCount > 0">
                    {{ formatNumber(searchResultUnitCount, '0,0') }}
                    {{ $t('messages.screensMatchingYourSearch') }}<br />
                    <small
                      >Showing {{ Math.min(unitPageInCurrentSearch * searchResultPageSize, searchResultUnitCount) }}
                      of
                      {{ searchResultUnitCount }}</small
                    >
                  </span>
                  <span v-else>
                    {{ capitalize($t('messages.noResultsFound')) }}
                  </span>
                </div>
                <ul
                  ref="unitList"
                  class="unit-list"
                  :class="{
                    'has-images':
                      activeUnits.filter((x) => x.imageUri != null || x.isStreetViewImageAvailable).length > 0,
                  }"
                  @scroll="handleUnitListScroll">
                  <li
                    v-for="unit in activeUnits"
                    :key="unit.id"
                    class="unit-list-item list-item"
                    @click="activateUnit(unit)">
                    <img
                      v-if="unit.imageUri != null"
                      :src="unit.imageUri" />
                    <img
                      v-else-if="unit.isStreetViewImageAvailable"
                      :src="unit.streetViewImageUrl" />
                    <div
                      v-else
                      class="icon">
                      <icon-colored-screen />
                    </div>
                    <div class="info-and-actions">
                      <div
                        class="info"
                        :title="unit.name">
                        <b>{{ unit.name }}</b>
                        <br /><span
                          v-if="isDemandOrganization"
                          :title="unit.description"
                          >{{ unit.description }}</span
                        >
                        <span v-else>[{{ unit.externalID }}]</span>
                      </div>
                      <div class="actions">
                        <button
                          v-if="!cart.isUnitInCart(unit)"
                          v-tooltip.right="capitalize($t('actions.addToFilters'))"
                          class="circle-btn scale-vertical-big-delay"
                          @click.stop="
                            () => {
                              addToCart([unit], true);
                            }
                          ">
                          <icon-add />
                        </button>
                        <button
                          v-else
                          v-tooltip.right="capitalize($t('actions.removeFromFilters'))"
                          class="inverse circle-btn scale-vertical-big-delay"
                          @click.stop="
                            () => {
                              removeFromCart([unit], true);
                            }
                          ">
                          <icon-delete />
                        </button>
                      </div>
                    </div>
                  </li>
                  <li
                    v-if="isSearchingUnits"
                    class="load-more-results">
                    <loader-small />
                    <span>{{ capitalize($t('messages.loadingMoreResults')) }}...</span>
                  </li>
                </ul>
              </div>

              <!-- UNIT DETAILS -->
              <UnitDetailsCard
                v-else-if="activeUnit"
                :active-units="activeUnits" />
            </div>

            <!-- Networks Legend -->
            <div
              v-if="showNetworks && !isActiveUnitsPaneVisible"
              class="network-colors-legend">
              <div class="header">{{ capitalize($t('names.network', { n: 2 })) }}</div>
              <div class="list">
                <table>
                  <tbody>
                    <tr
                      v-for="networkColor in colorPerNetworkID"
                      :key="networkColor.id">
                      <td>
                        <div :style="`background-color: ${networkColor.color}; border-radius: 2px`" />
                      </td>
                      <td>
                        <span>{{ activeNetworks.find((x) => x.id === networkColor.id).name }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="!isReferenceDataReady && !isLoadingUnits"
      class="loading">
      <div>
        <loader-small />
        <span>{{ $t('messages.gettingYourMapReady') }}</span>
      </div>
    </div>
    <file-upload
      ref="fileUpload"
      :is-screens-file-upload="isScreensUploadModalVisible"
      :is-sites-file-upload="isSitesUploadModalVisible"
      :allow-screen-and-site-exclusion="lineItem != null || creative != null"
      @screen-i-ds-imported="screenIDsImported"
      @site-i-ds-imported="siteIDsImported" />

    <modal
      id="missing_screen_ids_modal"
      :visible="isMissingScreenIDsModalVisible"
      :title="capitalize($t('messages.missingScreenIDsOrUnitIDs'))"
      :body="capitalize($t('messages.missingScreenIDsOrUnitIDsBody')) + ':'"
      :button1-caption="capitalize($t('actions.ok'))"
      @cancelled="isMissingScreenIDsModalVisible = false"
      @confirmed="isMissingScreenIDsModalVisible = false">
      <template #modal-after-form>
        <div class="missing-ids-list">
          <ul v-if="missingScreenIDs.length > 0">
            <li
              v-for="(col, i) in missingScreenIDs"
              :key="i">
              {{ col }}
            </li>
          </ul>
        </div>
      </template>
    </modal>

    <modal
      id="missing_site_ids_modal"
      :visible="isMissingSiteIDsModalVisible && missingSiteIDs?.length > 0"
      :title="capitalize($t('messages.missingSiteIDs'))"
      :body="capitalize($t('messages.missingSiteIDsBody'))"
      :button1-caption="capitalize($t('actions.ok'))"
      @cancelled="isMissingSiteIDsModalVisible = false"
      @confirmed="isMissingSiteIDsModalVisible = false">
      <template #modal-after-form>
        <div class="missing-ids-list">
          <ul v-if="missingSiteIDs.length > 0">
            <li
              v-for="(col, i) in missingSiteIDs"
              :key="i">
              {{ col }}
            </li>
          </ul>
        </div>
      </template>
    </modal>

    <modal
      :id="'max_screen_upload_warning_modal'"
      :visible="showMaxScreenUploadWarningModal"
      :title="capitalize($t('messages.maxScreenUploadLimitTitle'))"
      :body="capitalize($t('messages.maxScreenUploadLimitBody', [formatNumber(importedUnitsMax, '0,0')]))"
      :button1-caption="$t('actions.ok')"
      @confirmed="() => (showMaxScreenUploadWarningModal = false)"
      @cancelled="() => (showMaxScreenUploadWarningModal = false)" />

    <proximity-geofence-modal
      ref="proximityGeofenceModal"
      @proximity-geofence-created="onProximityGeofenceCreated" />
  </div>
</template>

<script type="text/babel">
  import turfBbox from '@turf/bbox';
  import turfBboxPolygon from '@turf/bbox-polygon';
  import turfTransformScale from '@turf/transform-scale';
  import axios from 'axios';
  import { DateTime } from 'luxon';
  import { capitalize, markRaw } from 'vue';
  import { mapActions, mapGetters, mapState } from 'vuex';

  import Colors from '@/Colors';
  import GoogleAnalyticsApi from '@/common/apis/GoogleAnalyticsApi';
  import { DropDown, DropDownGroup, LoaderSmall } from '@/common/components/controls';
  import UnitDetailsCard from '@/common/components/map/UnitDetailsCard.vue';
  import { MapInventoryType } from '@/common/enums/map.enums';
  import { IconColoredCPM, IconColoredDemographic, IconColoredScreen, IconColoredSite } from '@/common/icons/colored';
  import { IconAdd2, IconDelete, IconGeofenceGroup, IconSad, IconSearch, IconUpload } from '@/common/icons/monochrome';
  import { Language, MediaType, Network } from '@/common/models';
  import CreativeFormatOptions from '@/common/models/CreativeFormatOptions';
  import CreativeOrientationOptions from '@/common/models/CreativeOrientationOptions';
  import GeofenceGroup from '@/common/models/geo/GeofenceGroup';
  import GeofenceGroupType from '@/common/models/geofenceGroups/GeofenceGroupType.enum';
  import IABCategories from '@/common/models/iab-category/IABCategories';
  import LineItem from '@/common/models/lineitem/LineItem';
  import UnitTag from '@/common/models/misc-api-models/UnitTag';
  import Package from '@/common/models/package/Package';
  import Filter from '@/common/models/query-params/Filter';
  import FilterCollection from '@/common/models/query-params/FilterCollection';
  import * as FilterComparisonOperator from '@/common/models/query-params/FilterComparisonOperator';
  import FilterGroup from '@/common/models/query-params/FilterGroup';
  import LogicalOperator from '@/common/models/query-params/FilterLogicalOperator.enum';
  import QueryParams from '@/common/models/query-params/QueryParams';
  import ErrorHandler from '@/ErrorHandler';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import {
    areArraysEqual,
    FullDaypart,
    loadGooglePlacesApi,
    region,
    Regions,
    roundToSixDecimals,
    toServerDateTime,
    uniq,
  } from '@/globals';
  import BaiduMapWrapper from '@/org-app/components/inventory/overallMap/BaiduMapWrapper';
  import MapboxMap from '@/org-app/components/inventory/overallMap/MapboxMap';
  import MapTargetingSummary from '@/org-app/components/inventory/overallMap/MapTargetingSummary';
  import { useMediaOwners, useMediaTypes, useNetworks } from '@/org-app/services/tanstack/queries';
  import { runQueryInScope, waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
  import SessionCache from '@/SessionCache';
  import { delay, waitUntil } from '@/utils/DelayHelper';
  import MapDataProvider from '@/utils/MapDataProvider';
  import formatNumber from '@/utils/NumberFormatter';
  import ThrottledTaskRunner from '@/utils/ThrottledTaskRunner';
  import i18n from '@/vueI18n';

  import FileUpload from './FileUpload';
  import MapCart from './MapCart';
  import MapPanes from './MapPanes.vue';
  import OverallMapDropdownFilter from './OverallMapDropdownFilter';
  import ProximityGeofenceModal from './ProximityGeofenceModal.vue';

  export default {
    components: {
      UnitDetailsCard,
      BaiduMapWrapper,
      DropDown,
      DropDownGroup,
      FileUpload,
      IconAdd: IconAdd2,
      IconColoredScreen,
      IconDelete,
      IconSad,
      IconUpload,
      LoaderSmall,
      MapboxMap,
      MapPanes,
      MapTargetingSummary,
      ProximityGeofenceModal,
    },
    props: {
      showNetworks: {
        type: Boolean,
        default: false,
      },
      enableScrollZoom: {
        type: Boolean,
        default: true,
      },
      fitBoundsRightPadding: {
        type: Number,
        default: 24,
      },
      lineItem: {
        type: Object,
        default: null,
      },
      creative: {
        type: Object,
        default: null,
      },
      displayUnitPackFilter: {
        type: Boolean,
        default: false,
      },
      unitPack: {
        type: Object,
        default: null,
      },
      network: {
        type: Object,
        default: null,
      },
      isReadOnly: {
        type: Boolean,
        default: false,
      },
      areCreativeFormatsReadOnly: {
        type: Boolean,
        default: false,
      },
      areCreativeOrientationsReadOnly: {
        type: Boolean,
        default: false,
      },
      areIabCategoriesReadOnly: {
        type: Boolean,
        default: false,
      },
      areNetworksReadOnly: {
        type: Boolean,
        default: false,
      },
      areSitesReadOnly: {
        type: Boolean,
        default: false,
      },
      areUnitsReadOnly: {
        type: Boolean,
        default: false,
      },
      areLocationsReadOnly: {
        type: Boolean,
        default: false,
      },
      areTagsReadOnly: {
        type: Boolean,
        default: false,
      },
      areMediaTypesReadOnly: {
        type: Boolean,
        default: false,
      },
      areMarketsReadOnly: {
        type: Boolean,
        default: false,
      },
      areCitiesReadOnly: {
        type: Boolean,
        default: false,
      },
      areProximityGeofenceGroupsReadOnly: {
        type: Boolean,
        default: false,
      },
      enableMapBoxFullScreenButton: {
        type: Boolean,
        default: false,
      },
      enableMapBoxGeocoder: {
        type: Boolean,
        default: false,
      },
      showDropDownFilterOptions: {
        type: Boolean,
        default: true,
      },
      unitIDs: {
        type: Array,
        default: () => [],
      },
      enableTargetingSummary: {
        type: Boolean,
        default: false,
      },
      showActiveScreens: {
        type: Boolean,
        default: false,
      },
      loadAllOptionsForFilters: {
        type: Boolean,
        default: false,
      },
      mapControlPosition: {
        type: String,
        default: 'top-right',
      },
      clusteredType: {
        type: String,
        default: MapInventoryType.UNITS,
      },
      showNewUI: {
        type: Boolean,
        default: false,
      },
      estimatedScreenCount: {
        type: Number,
        default: null,
      },
      ignoreBuyTypeRestrictions: {
        type: Boolean,
        default: false,
      },
      isFromCreativePendingApprovals: {
        type: Boolean,
        default: false,
      },
      suppliers: {
        type: Array,
        default: null,
      },
      isEditing: {
        type: Boolean,
        default: false,
      },
      sharedPlanMapUUID: {
        type: String,
        default: null,
      },
      password: {
        // Only for public page
        type: String,
        default: null,
      },
      allowVenueTypeSelectParentOnly: {
        type: Boolean,
        default: true,
      },
      requestedForUTC: {
        type: DateTime,
        default: null,
      },
      targetAllLocations: {
        // Use for budget breakdown
        type: Boolean,
        default: false,
      },
    },
    emits: [
      'showViewSummary',
      'unitsLoading',
      'unitsLoaded',
      'unitsLoadError',
      'hasStartedFiltering',
      'initCart',
      'filterSelectionChanged',
      'onMapFilterSelectionChanged',
      'onMapFilterDropDownHidden',
      'onMapFilterDropDownVisible',
      'filterDropDownVisible',
      'filterDropDownHidden',
      'cartChanged',
      'loaded',
    ],
    data() {
      return {
        activeLocationMinDistanceFromLeft: 350,
        activeSite: null,
        activeSites: null,
        activeUnit: null,
        activeUnits: null,
        allCities: null,
        allCountries: null,
        allMarkets: null,
        /**
         * A list of all the available networks in the dropdown filter
         * @type {Network[]}
         */
        availableNetworks: null,
        allRegions: null,
        allTags: null,
        allCreativeFormats: null,
        allCreativeOrientations: null,
        allIabCategories: null,
        areActiveSitesFromMapInteraction: true,
        areActiveUnitsFromMapInteraction: true,
        areMediaTypesDataReady: false,
        autoApplyCartToFilters: true,
        availableFilters: [],
        availableMediaTypeIDs: null,
        availableUnitPackIDs: [],
        cancelLoadSitesTokenSource: null,
        cancelLoadUnitsTokenSource: null,
        cart: new MapCart(),
        clusters: [],
        colors: Colors,
        colorPerNetworkID: [],
        combineSearchResults: true,
        cpmInfo: null,
        cpmInfoForAllInventory: null,
        DateTime,
        daypart: FullDaypart,
        defaultExcludedTags: [],
        didFitToBoundsOnce: false,
        duplicateScreenIds: [],
        fetchLocations: true,
        filters: [],
        geofenceGroupForDrawnSelections: null,
        geofenceGroups: null,
        getFilteredLocationsThrottled: null,
        i18n,
        index: 0,
        IconColoredCPM,
        IconColoredDemographic,
        IconColoredScreen,
        IconColoredSite,
        iconGeofenceGroup: markRaw(IconGeofenceGroup),
        iconSearch: markRaw(IconSearch),
        isActiveUnitsPaneVisible: false,
        isFiltersPaneVisible: true,
        isFiltersRestored: false,
        isFirstClusterLoad: true,
        isLastEstimatedScreenCountNull: false,
        isLoadingUnits: false,
        isLoadingMoreSites: false,
        isLoadingMoreUnits: false,
        isLoaded: false,
        isMapStyleSatellite: false,
        isMapUpdated: false,
        isMissingScreenIDsModalVisible: false,
        isMissingSiteIDsModalVisible: false,
        isReady: false,
        isReferenceDataReady: false,
        isRunningGeofenceQuery: false,
        isScreensUploadModalVisible: false,
        isSitesUploadModalVisible: false,
        isViewSummaryVisible: false,
        locationPolygonFeatures: [],
        locationsWithShapesInCart: [],
        locations: [],
        locationExclusions: [],
        marketPolygonFeatures: [],
        mapContainerActualHeight: 100,
        mediaOwnerCountryRoots: {},
        mediaOwnersQuery: [],
        mediaTypes: [],
        missingScreenIDs: [],
        missingSiteIDs: [],
        networksQuery: [],
        networksPublicQuery: [],
        networksColorPalette: [
          '#FF0083',
          '#03A9F4',
          '#49D6BE',
          '#FFDF02',
          '#52489C',
          '#6E0D25',
          '#454545',
          '#FE5D26',
          '#0A100D',
          '#084C61',
          '#6D3D14',
          '#5F7367',
          '#0A369D',
          '#FF9770',
          '#335145',
          '#50723C',
        ],
        networkCountByClusterPerSite: {},
        /**
         * A list of all the active networks
         * @type {Network[]}
         */
        activeNetworks: [],
        params: {},
        proximityDropDownOpacity: 1,
        searchResultLocationCount: 0,
        searchResultMarketCount: 0,
        searchResultNetworkCount: 0,
        searchResultPageSize: 10,
        searchResultSiteCount: 0,
        searchResultUnitCount: 0,
        selectedFilters: [],
        selectedScreenUploadOption: 'include',
        selectedSiteUploadOption: 'include',
        sessionCache: SessionCache,
        showMaxScreenUploadWarningModal: false,
        showNetworksFilter: false,
        showNoSitesFoundMsg: false,
        showNoUnitsFoundMsg: false,
        showNoUnitPacksFoundMsg: false,
        sitesLoaded: [],
        sitesTotalCount: 0,
        sitesFromSearchLoaded: [],
        sitesFromSearchTotalCount: 0,
        siteImageUrl: null,
        sitesInDrawnGeofencesNotIncludedByAnyOtherFilters: [],
        sitesSearchTerm: '',
        tagsForFilters: [],
        tagsTotalCount: 0,
        unitPageInCurrentSearch: 0,
        units: [],
        unitsInDrawnGeofencesNotIncludedByAnyOtherFilters: [],
        unitsLoaded: [],
        unitsTotalCount: 0,
        unitsFromSearchLoaded: [],
        unitsFromSearchTotalCount: 0,
        unitsSearchTerm: '',
        updateIndexForUnitPack: 0,
        unitPacksForFilters: [],
        unitPacksTotalCount: 0,
        hasStartedFiltering: false,
        window,
        zoomLevel: 0,
        zoomIndex: 0,
      };
    },
    computed: {
      OverallMapDropdownFilter() {
        return OverallMapDropdownFilter;
      },
      ...mapGetters(['membershipSettings', 'hasChosenExchange', 'importedUnitsMax']),
      ...mapGetters('orgApp', [
        'isSupplyOrganization',
        'isDemandOrganization',
        'isCuratorOrganization',
        'isStroeerOrganization',
        'isSidekickOrganization',
      ]),
      ...mapState('orgApp', {
        organization: (state) => state.organization,
        organizationSettings: (state) => state.organizationSettings,
        languages: (state) => state.ReferenceData.languages,
      }),
      availableLanguages() {
        // id 0 is used for all / universal.
        // It doesn't make sense to show it in the dropdown and sending an id of 0 breaks the calls to the backend
        return [...this.languages].filter((l) => l.id > 0);
      },
      mediaOwners() {
        return this.mediaOwnersQuery.items;
      },
      networks() {
        return this.mapDataProvider.isPublicPlan
          ? this.networksPublicQuery.items
          : this.networksQuery.items?.filter((network) => network.isActive);
      },
      advertiser() {
        if (this.lineItem) {
          return this.lineItem?.campaign?.advertiser;
        }

        if (this.creative) {
          return this.creative?.advertiser;
        }

        return null;
      },
      map() {
        return this.isNotChina ? this.$refs.mapboxMap : this.$refs.baiduMap;
      },
      geofenceGroupPolygonFeatures() {
        return this.geofenceGroupFilters
          .map((x) => {
            if (this.cart.excludedGeofenceGroupIDs.includes(x.id)) {
              return [];
            }
            return x.geofences.map((g) => g.feature);
          })
          .reduce((mem, x) => [...mem, ...x], []);
      },
      geofenceGroupCentroidFeatures() {
        return this.geofenceGroupFilters
          .map((x) => {
            if (this.cart.excludedGeofenceGroupIDs.includes(x.id)) {
              return [];
            }
            return x.geofences.map((g) => g.pointFeature);
          })
          .reduce((mem, x) => [...mem, ...x], []);
      },
      isNotChina() {
        return region !== Regions.China;
      },
      entity() {
        if (this.lineItem != null) {
          return this.lineItem;
        }
        if (this.creative != null) {
          return this.creative;
        }
        if (this.unitPack != null) {
          return this.unitPack;
        }
        if (this.network != null) {
          // Unit Packs, networks doesn't have any info on unit ids in the pack.
          // So to make it work just initializing with empty values.
          const ent = this.network;
          const networkIDs = this.showDropDownFilterOptions ? [] : [ent.id];
          const unitPackIDs = [ent.id];

          return {
            ...ent,
            unitIDs: this.showDropDownFilterOptions ? this.unitIDs : [],
            unitExclusionIDs: [],
            siteIDs: [],
            siteExclusionIDs: [],
            locationIDs: [],
            locationExclusionIDs: [],
            marketIDs: [],
            marketExclusionIDs: [],
            geofenceGroupIDs: [],
            geofenceGroupExclusionIDs: [],
            mediaTypeIDs: [],
            mediaTypeExclusionIDs: [],
            networkIDs,
            networkExclusionIDs: [],
            unitTags: [],
            unitTagExclusions: [],
            unitPackIDs,
            unitPackExclusionIDs: [],
            creativeFormats: [],
            creativeFormatExclusions: [],
          };
        }
        return null;
      },
      showUnitsDropDownFilter() {
        return (
          this.membershipSettings != null &&
          (this.membershipSettings.uiEnableLineItemUnitTargeting ||
            this.isCuratorOrganization ||
            (this.isSupplyOrganization &&
              (this.displayUnitPackFilter || this.network != null || this.unitPack != null)))
        );
      },
      showExcludeOption() {
        // exclusions are not supported for DV LIT
        return (
          (this.lineItem != null && !this.isDisplayVideoLineItem) || this.creative != null || this.unitPack != null
        );
      },
      activeUnitScreenDimensionStyle() {
        const maxWidth = 220;
        const maxHeight = 110;
        let useHorizontalLayout = false;
        let width = this.activeUnit?.screenWidth ?? maxWidth;
        let height = this.activeUnit?.screenHeight ?? maxHeight;
        if (this.activeUnit.screenWidth > this.activeUnit.screenHeight) {
          height *= maxWidth / this.activeUnit.screenWidth;
          width = maxWidth;

          if (width / height > 1.5) {
            useHorizontalLayout = true;
          }
        } else {
          width *= maxHeight / this.activeUnit.screenHeight;
          height = maxHeight;
        }
        const style = `width: ${width}px; height: ${height}px;`;
        return useHorizontalLayout ? `${style}flex-direction: row;` : style;
      },
      marketFilters() {
        return this.filters.filter((x) => MapCart.isMarket(x));
      },
      networkFilters() {
        return this.filters.filter((x) => MapCart.isNetwork(x));
      },
      mediaTypeFilters() {
        return this.filters.filter((x) => MapCart.isMediaType(x));
      },
      geofenceGroupFilters() {
        return this.filters.filter((x) => MapCart.isGeofenceGroup(x));
      },
      locationFilters() {
        return this.filters.filter((x) => MapCart.isLocation(x));
      },
      sitesForFilters() {
        return this.sitesSearchTerm?.length > 0 ? this.sitesFromSearchLoaded : this.sitesLoaded;
      },
      sitesForFiltersTotalCount() {
        return this.sitesSearchTerm?.length > 0 ? this.sitesFromSearchTotalCount : this.sitesTotalCount;
      },
      unitsForFilters() {
        return this.unitsSearchTerm?.length > 0 ? this.unitsFromSearchLoaded : this.unitsLoaded;
      },
      unitsForFiltersTotalCount() {
        return this.unitsSearchTerm?.length > 0 ? this.unitsFromSearchTotalCount : this.unitsTotalCount;
      },
      layerSettingsPaneStyle() {
        if (this.showSettingsFor === 'cart') {
          return 'right: 35.5rem; top: 1.6rem;';
        }
        return `left: 3.2rem; top: ${this.lastClickPosition.y}px;`;
      },
      isSearching() {
        return (
          this.isSearchingLocations ||
          this.isSearchingMarkets ||
          this.isSearchingNetworks ||
          this.isSearchingSites ||
          this.isSearchingUnits
        );
      },
      unitDetailsPaneStyle() {
        return `max-height: ${this.mapContainerActualHeight - 15}px`;
      },
      filteredMediaTypes() {
        if (this.availableMediaTypeIDs == null) {
          return [];
        }
        return this.mediaTypes;
      },
      doesClustersContainNoSites() {
        return this.showNetworks && this.clusters[0]?.sites == null;
      },
      clusterSites() {
        return this.showNetworks ? this.clusters?.map((x) => x.sites) : [];
      },
      uniqueNetworkIDs() {
        if (this.clusterSites == null || this.clusterSites.every((cluster) => cluster == null)) {
          return [];
        }
        return this.clusterSites?.length
          ? uniq(
              this.clusterSites
                .map((x) => Object.values(x))
                .map((x) => x.map((y) => y.map((z) => z.network_id)))
                .flat()
                .flat(),
            )
          : [];
      },
      geocodedLocationsGeoJson() {
        // If Cluster Type is set to sites and clusters does not contain any sites then an empty geoJSON is returned.
        if (this.clusters == null || this.doesClustersContainNoSites) {
          return {
            type: 'FeatureCollection',
            features: [],
          };
        }

        let features = [];
        if (this.showNetworks) {
          for (const cluster of this.clusters) {
            const networks = Object.values(cluster.sites).reduce((mem, siteNetworks) => {
              siteNetworks.forEach((n) => {
                if (mem[n.network_id] == null) {
                  mem[n.network_id] = n.unit_count;
                } else {
                  mem[n.network_id] += n.unit_count;
                }
              });
              return mem;
            }, {});

            features.push({
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [cluster.longitude, cluster.latitude],
              },
              properties: {
                clusterID: cluster.id,
                siteIDs: Object.keys(cluster.sites),
                siteCount: Object.keys(cluster.sites).length,
                networks,
              },
            });
          }
        } else {
          features = this.clusters.map((x) => {
            return {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [x.longitude, x.latitude],
              },
              id: x.id, // This is used by mapbox setFeatureState()
              properties: {
                id: x.id, // This is used by GeofenceListAndMap.activateGeofence()
                bounds: x.bounds,
                expansionZoom: x.expansion_zoom,
                count: x.count,
                countFormatted: x.count >= 1000 ? formatNumber(x.count, '0.0a') : `${x.count}`,
                unitIDs: x.unit_ids,
                siteIDs: x.site_ids,
                lat: x.latitude,
                lng: x.longitude,
                latLngString: `${x.latitude},${x.longitude}`,
                isActive:
                  this.activeUnit != null &&
                  roundToSixDecimals(x.latitude) === roundToSixDecimals(this.activeUnit.unitOrSiteLat) &&
                  roundToSixDecimals(x.longitude) === roundToSixDecimals(this.activeUnit.unitOrSiteLng),
              },
            };
          });
        }
        return {
          type: 'FeatureCollection',
          features,
        };
      },
      mediaOwnerIDs() {
        if (this.entity != null) {
          if (this.isSupplyOrganization) {
            return [this.organization.id];
          } else if (this.isCuratorOrganization) {
            return this.suppliers?.map((x) => x.id);
          }
          if (this.lineItem != null && this.lineItem.mediaOwnerIDs?.length > 0) {
            return this.lineItem.mediaOwnerIDs;
          }
        }
        if (this.showActiveScreens || this.entity != null) {
          return this.mediaOwners.filter((x) => x.isActive).map((mediaOwner) => mediaOwner.id);
        }
        return [];
      },
      packageID() {
        if (this.lineItem instanceof Package) {
          return this.lineItem.id;
        }
        if (this.lineItem instanceof LineItem) {
          return this.lineItem.package?.id;
        }
        return null;
      },
      isLineItemTypePackage() {
        if (this.lineItem == null) {
          return false;
        }
        return this.lineItem.type === 'Package';
      },
      showDropDownFilters() {
        return this.showDropDownFilterOptions;
      },
      areAllFiltersRemoved() {
        return (
          !this.cart.locationsInCart?.length &&
          !this.cart.marketsInCart?.length &&
          !this.cart.mediaTypesInCart?.length &&
          !this.cart.networksInCart?.length &&
          !this.cart.regionsInCart?.length &&
          !this.cart.sitesInCart?.length &&
          !this.cart.tagsInCart?.length &&
          !this.cart.unitPacksInCart?.length &&
          !this.cart.unitsInCart?.length &&
          !this.cart.geofenceGroupsInCart?.length &&
          !this.cart.geofencesInCart?.length &&
          !this.cart.creativeFormatsInCart?.length &&
          !this.cart.countriesInCart?.length &&
          !this.cart.citiesInCart?.length &&
          !this.cart.languagesInCart?.length &&
          !this.cart.creativeFormatsInCart?.length &&
          !this.cart.iabCategoriesInCart?.length
        );
      },
      isIABFilterEnabled() {
        return this.selectedFilters.find((x) => x.id === OverallMapDropdownFilter.IabCategories) != null;
      },
      isDisplayVideoLineItem() {
        return this.lineItem?.isChannelTypeDisplayVideo ?? false;
      },
      canShowUnitPacks() {
        return this.isSupplyOrganization && this.displayUnitPackFilter;
      },
    },
    watch: {
      async activeUnit(val) {
        if (val !== null) {
          await delay(100);
          val.checkStreetViewImageAvailability();
          this.map.displayActiveSiteOrUnitOnMap(val);
        } else {
          this.map.clearActiveSiteOrUnitFromMap();
        }
      },
      isLoadingUnits(val) {
        if (val) {
          this.$emit('unitsLoading');
        } else {
          this.$emit('unitsLoaded');
        }
      },
      locationFilters() {
        this.fetchLocationPolygonFeatures();
      },
      marketFilters() {
        this.fetchMarketPolygonFeatures();
      },
      filteredMediaTypes(val) {
        // refresh available filters and select venue type filter if available
        this.initFilters();
        const venueTypeFilter = this.availableFilters.find((f) => f.id === 'dd_media_types');
        if (
          (val.length > 0 && venueTypeFilter != null && !this.selectedFilters.some((f) => f.id === 'dd_media_types')) ||
          this.entity.mediaTypeIDs?.length > 0 ||
          this.entity.mediaTypeExclusionIDs?.length > 0
        ) {
          this.updateSelectedFilters({ included: [...this.selectedFilters, venueTypeFilter] });
        }
      },
      clusteredType() {
        this.getFilteredLocations();
        this.isActiveUnitsPaneVisible = false;
      },
      // Resets the filters and the map when the mediaOwnersIds changes.
      // Otherwise, the network filter might be missing because the lineItem.mediaOwnerIds is set after the `OverallMap` component is loaded.
      // @see https://hivestack.atlassian.net/browse/CAM-2352

      'lineItem.mediaOwnerIDs': function (newValue, oldValue) {
        // Only reload the map when necessary (ie: the lineItem mediaOwnersIDs is first loaded)
        if (!(oldValue?.length === 0 && newValue?.length > 0)) {
          return;
        }
        this.initFilters();
        this.initMap();
      },
      hasStartedFiltering(val) {
        this.$emit('hasStartedFiltering', val);
      },
      isDisplayVideoLineItem() {
        this.clearFilters();
        this.initFilters();
        this.initMap();
      },
    },
    async mounted() {
      this.mapDataProvider = new MapDataProvider(this.entity);

      if (!this.mapDataProvider.isPublicPlan) {
        this.mediaOwnersQuery = useMediaOwners();
        this.networksQuery = useNetworks();
        await waitForPendingQueries([this.mediaOwnersQuery, this.networksQuery]);
      }

      loadGooglePlacesApi();

      this.initFilters();
      this.initMap();

      await Promise.allSettled([this.loadMediaOwners(), this.loadMediaTypes()]);

      if (this.loadAllOptionsForFilters) {
        // Sync Task
        this.loadCreativeFormats();
        this.loadCreativeOrientations();
        this.loadIabCategories();

        // Async Task
        // Use the /filterchanges endpoint to fetch what are the available IDs for each map filter.
        // This needs to be done outside the Promise.all because subsequent calls depend on these IDs
        await this.loadAvailableIDsPerFilterType();

        const promises = [
          this.loadTags(),
          this.loadActiveNetworks(),
          this.loadGeofenceGroups(),
          this.loadMarkets(),
          this.loadAvailableNetworks(),
          this.loadLanguages(),
        ];

        if ((this.showDropDownFilters && !this.isReadOnly) || this.targetAllLocations) {
          /**
           * Loads the expanded locations then loads the Countries, Regions, Cities filters in parallel
           */
          const loadAllLocations = async () => {
            await this.loadExpandedLocations(); // Needed first to load Countries, Regions and Cities
            await Promise.allSettled([this.loadCountries(), this.loadRegions(), this.loadCities()]);
          };
          promises.push(loadAllLocations());
        }

        await Promise.allSettled(promises);
      }

      this.$emit('initCart', this.cart);

      this.isLoaded = true;
      this.$emit(GlobalEvents.loaded);
    },
    beforeUnmount() {
      this.fetchLocations = false;
      this.cancelLoadCitiesTokenSource?.cancel();
      this.cancelLoadRegionsTokenSource?.cancel();
      this.cancelLoadCountriesTokenSource?.cancel();
      window.removeEventListener('resize', this.syncMapContainerActualHeight);
      this.getFilteredLocationsThrottled?.destroy();
    },
    methods: {
      ...mapActions([
        'updateGeofenceGroupGeofences',
        'updateCreativeCreativeFormats',
        'updateCreativeCreativeOrientations',
        'updateCreativeCreativeFormatExclusions',
        'updateCreativeCreativeOrientationExclusions',
        'updateCreativeGeofenceGroupIDs',
        'updateCreativeGeofenceGroupExclusionIDs',
        'updateCreativeLocationExclusionIDs',
        'updateCreativeLocationIDs',
        'updateCreativeMarketIDs',
        'updateCreativeMarketExclusionIDs',
        'updateCreativeMediaTypeIDs',
        'updateCreativeMediaTypeExclusionIDs',
        'updateCreativeNetworkIDs',
        'updateCreativeNetworkExclusionIDs',
        'updateCreativeSiteExclusionIDs',
        'updateCreativeSiteIDs',
        'updateCreativeUnitExclusionIDs',
        'updateCreativeUnitTags',
        'updateCreativeUnitTagExclusions',
        'updateCreativeUnitIDs',
        'updateCreativeUnitPackIDs',
        'updateCreativeUnitPackExclusionIDs',
        'updateLineItemCreativeFormats',
        'updateLineItemCreativeOrientations',
        'updateLineItemCreativeFormatExclusions',
        'updateLineItemCreativeOrientationExclusions',
        'updateLineItemGeofenceGroupIDs',
        'updateLineItemGeofenceGroupExclusionIDs',
        'updateLineItemIabCategories',
        'updateLineItemIabCategoryExclusions',
        'updateLineItemLocationExclusionIDs',
        'updateLineItemLocationIDs',
        'updateLineItemMarketIDs',
        'updateLineItemMarketExclusionIDs',
        'updateLineItemLanguageIDs',
        'updateLineItemLanguageExclusionIDs',
        'updateLineItemMediaTypeIDs',
        'updateLineItemMediaTypeExclusionIDs',
        'updateLineItemNetworkIDs',
        'updateLineItemNetworkExclusionIDs',
        'updateLineItemSiteExclusionIDs',
        'updateLineItemSiteIDs',
        'updateLineItemUnitExclusionIDs',
        'updateLineItemUnitIDs',
        'updateLineItemUnitTags',
        'updateLineItemUnitTagExclusions',
        'updateLineItemUnitPackExclusionIDs',
        'updateLineItemUnitPackIDs',
        'updateSiteIsStreetViewImageAvailable',
        'updateSiteUnits',
        'updateUnitSite',
      ]),
      ...mapActions({
        getAvailableIDsPerFilterType: 'getAvailableIDsPerFilterType',
        getDemographics: 'getDemographics',
        getLanguages: 'getLanguages',
        getGeofenceGroups: 'getGeofenceGroups',
        getLocationsByLevel: 'getLocationsByLevel',
        getLocations: 'getLocations',
        getMarketsWithInventory: 'getMarketsWithInventory',
        getLocationRoots: 'getLocationRoots',
        getMarkets: 'getMarkets',
        getUnits: 'getUnits',
        getSites: 'getSites',
        getUnitPacks: 'getUnitPacks',
        getTags: 'getTags',
        getClusteredUnitsFromTargets: 'getClusteredUnitsFromTargets',
        getClusteredSiteNetworksFromTargets: 'getClusteredSiteNetworksFromTargets',
        getUnitIDsInGeofences: 'getUnitIDsInGeofences',
      }),
      ...mapActions('orgApp', ['getGeofenceGroup', 'getLanguages', 'getPublicPlanGeofenceGroups']),
      formatNumber,
      onFitBoundsTriggered() {
        //
        // set zoom level to 17 on initial load.
        //
        const zoomLevel = this.map?.getZoomLevel();
        if (this.zoomIndex === 0 && this.isNotChina && zoomLevel > 20 && !this.entity?.isNew) {
          this.zoomLevel = 17;
          this.map.setZoomLevel(17);
          this.zoomIndex += 1;
        }
      },
      async updateSiteImage() {
        let siteImageUrl = null;

        // Take the image of one the units in the active site if there's any.
        const firstUnitWithImage = this.activeUnits
          .filter((unit) => unit.siteID === this.activeSite.id)
          .find((unit) => unit.imageUri != null);

        if (firstUnitWithImage != null) {
          siteImageUrl = firstUnitWithImage.imageUri;
        } else {
          // If site doesn't have street view available, check to see if it's available
          if (!this.activeSite.isStreetViewImageAvailable) {
            await this.activeSite.checkStreetViewImageAvailability();
          }

          // If current site does end up having street view, apply the same truth
          // to all the sites in the same coordinates to not call the google maps api again
          if (this.activeSite.isStreetViewImageAvailable) {
            // Get all the sites with the same coordinates excluding the current site were at.
            const sitesWithSameCoordinates = this.activeSites.filter(
              (site) =>
                site.longitude === this.activeSite.longitude &&
                site.latitude === this.activeSite.latitude &&
                site.id !== this.activeSite.id,
            );
            sitesWithSameCoordinates.forEach((site) => {
              this.updateSiteIsStreetViewImageAvailable({ site, value: true });
            });
          }
          siteImageUrl = this.activeSite.isStreetViewImageAvailable ? this.activeSite.streetViewImageUrl : null;
        }
        this.siteImageUrl = siteImageUrl;
      },
      clearActiveUnits() {
        this.activeUnit = null;
        this.activeUnits = null;
        this.activeSite = null;
        this.activeSites = null;
      },
      onActiveUnitIDsChanged(items) {
        this.areActiveUnitsFromMapInteraction = true;
        this.activeUnits = null;
        this.activeUnit = null;

        this.activeSites = null;
        this.activeSite = null;
        this.searchResultUnitCount = items?.ids?.length || 0;

        // Greater than 100 may throw 431 Request Header Fields Too Large...
        if (this.searchResultUnitCount === 0 || items?.ids?.length > 100) {
          return;
        }

        if (this.queryParamsForGetUnits != null) {
          this.queryParamsForGetUnits.cancelTokenSource.cancel();
        }

        if (items.type === MapInventoryType.UNITS) {
          this.updateActiveUnits(items);
        } else if (items.type === MapInventoryType.SITES) {
          this.updateActiveSites(items);
        }
      },
      async updateActiveUnits({ ids }) {
        this.queryParamsForGetUnits = QueryParams.for({
          top: null,
          filterCollection: new FilterCollection([new Filter('unit_id', ids, FilterComparisonOperator.IN)]),
        });
        this.queryParamsForGetUnits.expand = [
          'site',
          'unit_languages',
          'media_type',
          'unit_iab_blacklist',
          'unit_advertiser_blacklist',
        ];
        this.queryParamsForGetUnits.showLoader = false;

        this.isSearchingUnits = true;
        this.isLoadingUnits = true;

        const result = await this.fetchUnits(this.queryParamsForGetUnits);
        if (result == null) {
          // Query was cancelled.
          return;
        }

        this.onActiveUnitsLoaded(result);

        await this.loadLanguages();
      },
      async updateActiveSites({ ids }) {
        this.queryParamsForGetUnits = QueryParams.for({
          top: null,
          filterCollection: new FilterCollection([new Filter('site_id', ids, FilterComparisonOperator.IN)]),
        });
        this.queryParamsForGetUnits.expand = ['unit_ids', 'unit_count', 'physical_unit_count', 'media_type'];
        this.queryParamsForGetUnits.showLoader = false;

        this.isSearchingUnits = true;
        this.isLoadingUnits = true;

        const sitesResult = await this.fetchSites(this.queryParamsForGetUnits);
        if (sitesResult == null) {
          // Query was cancelled.
          return;
        }

        this.activeSites = null;
        const unitsInSiteIDsResult = await this.fetchUnits(this.queryParamsForGetUnits);

        if (unitsInSiteIDsResult?.items?.length) {
          const unitItems = unitsInSiteIDsResult.items;
          const unitsData = { mediaTypeIDs: [], networkIDs: [], floorCPMs: [] };

          unitItems.forEach((unit) => {
            if (unit.mediaTypeID) {
              unitsData.mediaTypeIDs.push(unit.mediaTypeID);
            }
            if (unit.networkID) {
              unitsData.networkIDs.push(unit.networkID);
            }
            if (unit.floorCPM) {
              unitsData.floorCPMs.push(unit.floorCPM);
            }
          });

          const a = unitsInSiteIDsResult.items.find((x) => x.imageUri);
          this.activeUnit = a != null ? a : unitsInSiteIDsResult.items[0];
          this.activeUnits = unitsInSiteIDsResult.items;
        }
        if (sitesResult?.items?.length) {
          [this.activeSite] = sitesResult.items;
          this.activeSites = sitesResult.items;
          const selectedCluster = this.clusters.find((cluster) =>
            Object.keys(cluster.sites)
              .map((siteID) => parseInt(siteID, 10))
              .includes(this.activeSite.id),
          );
          this.networkCountByClusterPerSite = this.getNetworkCountByClusterPerSite(selectedCluster);
        }
        await this.onActiveUnitsLoaded();
      },
      getNetworkCountByClusterPerSite(selectedCluster) {
        const sites = {};
        Object.entries(selectedCluster.sites).forEach(([siteID, site]) => {
          const networkIDs = site.map((network) => network.network_id);
          const networkCounts = site.map((network) => network.unit_count);

          let networks = networkIDs
            .map((networkID, index) => ({
              id: networkID,
              count: networkCounts[index],
            }))
            .reduce(
              (accumulator, currentValue) => ({
                ...accumulator,
                [currentValue.id]: (accumulator[currentValue.id] || 0) + currentValue.count,
              }),
              {},
            );

          networks = new Map(Object.entries(networks).sort((a, b) => b[1] - a[1]));
          sites[siteID] = networks;
        });
        return sites;
      },
      async onActiveUnitsLoaded(result) {
        this.isSearchingUnits = false;
        this.isActiveUnitsPaneVisible = true;
        this.isLoadingUnits = false;
        this.queryParamsForGetUnits = null;
        if (result != null) {
          [this.activeUnit] = result.items;
          this.activeUnits = result.items;
        }
        if (this.clusteredType === MapInventoryType.SITES) {
          await this.updateSiteImage();
        }
      },
      async goToPreviousCard() {
        if (this.clusteredType === MapInventoryType.SITES) {
          this.activeSite =
            this.activeSites[
              (this.activeSites.length + this.activeSites.indexOf(this.activeSite) - 1) % this.activeSites.length
            ];
          await this.updateSiteImage();
        } else {
          this.activeUnit =
            this.activeUnits[
              (this.activeUnits.length + this.activeUnits.indexOf(this.activeUnit) - 1) % this.activeUnits.length
            ];
        }
      },
      async goToNextCard() {
        if (this.clusteredType === MapInventoryType.SITES) {
          this.activeSite = this.activeSites[(this.activeSites.indexOf(this.activeSite) + 1) % this.activeSites.length];
          await this.updateSiteImage();
        } else {
          this.activeUnit = this.activeUnits[(this.activeUnits.indexOf(this.activeUnit) + 1) % this.activeUnits.length];
        }
      },
      addToCart(collection, isExclusion = false) {
        const addedToCart = this.cart.add(collection, isExclusion);
        if (this.autoApplyCartToFilters && collection.length < 100) {
          addedToCart.forEach((x) => {
            this.addFilter(x);
          });
        }

        if (this.map != null) this.map.showActiveFeatures([]);
        this.hasStartedFiltering = !this.areAllFiltersRemoved;
      },
      removeFromCart(collection, isFromSearchResults = false, removeAll = false) {
        if (collection.length === 0) {
          return;
        }

        const cartItems = removeAll ? this.cart.removeAll(collection) : this.cart.remove(collection);

        cartItems
          .filter((x) => MapCart.isLocation(x))
          .forEach((x) => {
            const lwsic =
              this.locationsWithShapesInCart == null
                ? null
                : this.locationsWithShapesInCart.find((lws) => lws.id === x.id);
            if (lwsic != null) {
              this.locationsWithShapesInCart.splice(this.locationsWithShapesInCart.indexOf(lwsic), 1);
            }
          });
        cartItems
          .filter((x) => MapCart.isGeofenceGroup(x))
          .forEach((x) => {
            if (x === this.geofenceGroupForDrawnSelections) {
              this.geofenceGroupForDrawnSelections.geofences.splice(
                0,
                this.geofenceGroupForDrawnSelections.geofences.length,
              );
              this.geofenceGroupForDrawnSelections = null;
              this.unitsInDrawnGeofencesNotIncludedByAnyOtherFilters = [];
              this.sitesInDrawnGeofencesNotIncludedByAnyOtherFilters = [];
            }
          });

        collection.forEach((x) => {
          this.removeFilter(x, isFromSearchResults);
        });
        this.hasStartedFiltering = !this.areAllFiltersRemoved;
      },
      capitalize(s) {
        return capitalize(s);
      },
      showScreensCSVUploadModal() {
        this.isScreensUploadModalVisible = true;
        this.isSitesUploadModalVisible = false;
        this.$refs.fileUpload.show();
      },
      showSitesCSVUploadModal() {
        this.isScreensUploadModalVisible = false;
        this.isSitesUploadModalVisible = true;
        this.$refs.fileUpload.show();
      },
      activateUnit(unit, performZoom = false) {
        this.activeResultsType = MapInventoryType.UNITS;
        if (this.activeUnits == null) {
          this.activeUnits = [unit];
        }
        this.activeUnit = unit;
        if (!this.areActiveUnitsFromMapInteraction || performZoom) {
          const zoom = Math.max(this.map.getZoom(), 15);
          this.map.easeTo({
            center: [
              unit.longitude == null ? unit.site.longitude : unit.longitude,
              unit.latitude == null ? unit.site.latitude : unit.latitude,
            ],
            zoom,
            duration: 0,
          });
        } else {
          this.map.easeTo({
            center: [
              unit.longitude == null ? unit.site.longitude : unit.longitude,
              unit.latitude == null ? unit.site.latitude : unit.latitude,
            ],
            duration: 100,
          });
        }
        unit.checkStreetViewImageAvailability();
      },
      addFilter(filter, isFromSearchResults = false) {
        if (this.filters.indexOf(filter) > -1) {
          this.filters.splice(this.filters.indexOf(filter), 1);
        }
        this.filters.push(filter);

        this.updateMapAccordingToFilters(!isFromSearchResults);
      },
      addFilters(filters, isFromSearchResults = false) {
        this.filters = this.filters.filter((x) => !filters.includes(x)).concat(filters);

        this.updateMapAccordingToFilters(!isFromSearchResults);
      },
      removeFilter(filter, isFromSearchResults) {
        this.filters.splice(
          this.filters.findIndex((x) => x.id === filter.id),
          1,
        );

        this.updateMapAccordingToFilters(!isFromSearchResults);
      },
      async updateMapAccordingToFilters(fitToBounds) {
        if (this.isDisplayVideoLineItem) return;
        // Delay the call until the map is ready.
        await waitUntil(() => this.map != null);

        if (this?.organizationSettings?.uiEnableSelectTargetingToSeeScreens) {
          this.isFirstClusterLoad = false;
        }

        this.getFilteredLocations(fitToBounds);
      },
      async fetchMarketPolygonFeatures() {
        let features = [];
        const includedMarkets = this.marketFilters.filter((market) => this.cart.includedMarketIDs.includes(market.id));
        if (includedMarkets.length < 50) {
          const marketsWithoutGeometry = includedMarkets.filter((x) => x.geometry == null);
          if (marketsWithoutGeometry.length > 0) {
            const marketsWithGeometry = await this.getMarkets({
              queryParams: QueryParams.all(
                FilterCollection.for(
                  'market_id',
                  marketsWithoutGeometry.map((x) => x.id),
                  FilterComparisonOperator.IN,
                ),
              ),
            });
            marketsWithoutGeometry.forEach((m) => {
              m.geometry = marketsWithGeometry.items.find((x) => x.id === m.id).geometry;
            });
          }
          features = includedMarkets.map((x) => x.feature);
        }
        this.marketPolygonFeatures = features;
      },
      async fetchLocationPolygonFeatures() {
        if (this.locationFilters.length === 0) {
          this.locationPolygonFeatures = [];
          return;
        }

        const includedLocations = this.locationFilters.filter((location) =>
          this.cart.includedLocationIDs.includes(location.id),
        );

        if (includedLocations.length === 0) {
          this.locationPolygonFeatures = [];
          return;
        }

        const res = await this.getLocationsFromIds(
          includedLocations.map((x) => x.id),
          true,
        );

        const locationWithGeometry = res?.items.filter((x) => x.features != null) || [];

        this.locationPolygonFeatures = locationWithGeometry
          .map((x) => x.features)
          .reduce((mem, x) => [...mem, ...x], []);
      },
      async nextUnitSearchResultPage() {
        this.isSearchingUnits = true;
        this.lastUnitSearchQueryParams.skip = this.unitPageInCurrentSearch * this.searchResultPageSize;

        const result = await this.fetchUnits(this.lastUnitSearchQueryParams);
        this.areActiveUnitsFromMapInteraction = false;
        this.searchResultUnitCount = result.totalCount;
        if (this.activeUnits != null) {
          this.activeUnits = [...this.activeUnits, ...result.items];
        } else {
          this.activeUnits = result.items.slice(0, this.maxResultsPerCategory);
        }
        this.unitPageInCurrentSearch += 1;
        this.isSearchingUnits = false;

        await waitUntil(() => this.$refs.unitList != null);
        this.$refs.unitList?.dispatchEvent(new window.Event('scroll'));
      },
      async loadSites(searchTerm, skip) {
        if (this.entity == null) {
          return;
        }
        try {
          if (this.cancelLoadSitesTokenSource != null) {
            this.cancelLoadSitesTokenSource.cancel();
          }
          const ddSites = this.availableFilters.find((x) => x.id === 'dd_sites');
          if (skip > 0) {
            this.isLoadingMoreSites = true;
          } else if (ddSites) {
            ddSites.isLoading = true;
          }
          const trimmedSearchTerm = searchTerm?.trim() || '';
          this.cancelLoadSitesTokenSource = axios.CancelToken.source();
          const queryParams = QueryParams.for({
            top: 20,
            skip: skip,
            sort: 'name',
            include: ['site_id', 'name', 'external_id'],
            filterCollection: new FilterCollection(
              [
                new FilterGroup([
                  new Filter('name', [trimmedSearchTerm], FilterComparisonOperator.LIKE),
                  new Filter('external_id', [trimmedSearchTerm], FilterComparisonOperator.LIKE),
                ]),
                new Filter('owner_id', this.mediaOwnerIDs, FilterComparisonOperator.IN),
                new Filter('units.site_id', [null], FilterComparisonOperator.NOT_EQUAL),
                new Filter('units.active', [true], FilterComparisonOperator.EQUAL),
                new Filter('active', [true], FilterComparisonOperator.EQUAL),
              ],
              LogicalOperator.AND,
            ),
          });
          queryParams.showLoader = false;
          const result = await this.getSites({
            queryParams,
            cancelTokenSource: this.cancelLoadSitesTokenSource,
          });
          this.showNoSitesFoundMsg = result.totalCount === 0;
          if (searchTerm?.length > 0) {
            this.sitesFromSearchLoaded.push(...result.items);
            this.sitesFromSearchTotalCount = result.totalCount;
          } else {
            this.sitesLoaded.push(...result.items);
            this.sitesTotalCount = result.totalCount;
          }
          this.isLoadingMoreSites = false;
          if (ddSites) {
            ddSites.isLoading = false;
          }
        } catch (error) {
          ErrorHandler.handleExceptionInUIComponent(error);
        }
      },
      async loadTags() {
        if (this.entity == null || this.isSidekickOrganization) {
          return;
        }
        this.cancelLoadTagsTokenSource = axios.CancelToken.source();
        const params = {
          params: {
            owner_ids: this.mediaOwnerIDs,
            unit_targeting: true,
          },
          cancelTokenSource: this.cancelLoadTagsTokenSource,
          showLoader: false,
        };
        const result = await this.getTags(params);
        if (result != null) {
          const tags = result.map((x, i) => UnitTag.fromApi(x, i));
          this.cancelLoadTagsTokenSource = null;
          this.tagsTotalCount = tags.length;
          this.allTags = tags.sort((a, b) => a.name.localeCompare(b.name));
          this.tagsForFilters = tags.sort((a, b) => a.name.localeCompare(b.name));
          this.defaultExcludedTags = tags.filter((a) => a.excludedByDefault);
        }
      },
      async loadUnits(searchTerm, skip) {
        if (this.entity == null) {
          return;
        }
        try {
          if (this.cancelLoadUnitsTokenSource != null) {
            this.cancelLoadUnitsTokenSource.cancel();
          }
          const ddUnits = this.availableFilters?.find((x) => x.id === 'dd_units');
          if (skip > 0) {
            this.isLoadingMoreUnits = true;
          } else if (ddUnits) {
            ddUnits.isLoading = true;
          }
          const trimmedSearchTerm = searchTerm?.trim() || '';
          this.cancelLoadUnitsTokenSource = axios.CancelToken.source();
          const queryParams = QueryParams.for({
            top: 20,
            skip: skip,
            sort: 'name',
            include: ['unit_id', 'name', 'external_id'],
            filterCollection: new FilterCollection(
              [
                new FilterGroup([
                  new Filter('name', [trimmedSearchTerm], FilterComparisonOperator.LIKE),
                  new Filter('external_id', [trimmedSearchTerm], FilterComparisonOperator.LIKE),
                ]),
                new Filter('owner_id', this.mediaOwnerIDs, FilterComparisonOperator.IN),
                new Filter('site.active', [true], FilterComparisonOperator.EQUAL),
                new Filter('active', [true], FilterComparisonOperator.EQUAL),
              ],
              LogicalOperator.AND,
            ),
          });
          const result = await this.getUnits({
            queryParams,
            cancelTokenSource: this.cancelLoadUnitsTokenSource,
          });
          this.showNoUnitsFoundMsg = result.totalCount === 0;
          if (searchTerm?.length > 0) {
            this.unitsFromSearchLoaded.push(...result.items);
            this.unitsFromSearchTotalCount = result.totalCount;
          } else {
            this.unitsLoaded.push(...result.items);
            this.unitsTotalCount = result.totalCount;
          }
          this.isLoadingMoreUnits = false;
          if (ddUnits) {
            ddUnits.isLoading = false;
          }
        } catch (error) {
          ErrorHandler.handleExceptionInUIComponent(error);
        }
      },
      async loadUnitPacks(searchTerm) {
        if (!this.canShowUnitPacks) {
          return;
        }
        this.showNoUnitPacksFoundMsg = false;
        const computedSearchTerm = searchTerm || '';
        const ddUnitPacks = this.selectedFilters.find((x) => x.id === 'dd_unitpacks');
        ddUnitPacks.isLoading = true;

        this.cancelLoadUnitPacksTokenSource = axios.CancelToken.source();
        const queryParams = QueryParams.for({
          top: null,
          include: ['unitpack_id', 'name'],
          filterCollection: new FilterCollection(
            [
              new Filter('name', [computedSearchTerm], FilterComparisonOperator.LIKE),
              new Filter('owner_id', this.mediaOwnerIDs, FilterComparisonOperator.IN),
              new Filter('active', [true], FilterComparisonOperator.EQUAL),
            ],
            LogicalOperator.AND,
          ),
        });
        const result = await this.getUnitPacks({
          queryParams,
          cancelTokenSource: this.cancelLoadUnitPacksTokenSource,
          showLoader: false,
        });
        this.unitPacksForFilters = result.items
          .filter((a) => this.availableUnitPackIDs.includes(a.id))
          .sort(this.nameCompare);
        this.unitPacksTotalCount = this.unitPacksForFilters.length;

        if (computedSearchTerm?.length > 0 && this.unitPacksTotalCount === 0) {
          this.showNoUnitPacksFoundMsg = true;
        }
        ddUnitPacks.isLoading = false;
      },
      async loadLocations(locationLevel, locationCancelToken) {
        if (this.entity == null) {
          return [];
        }
        const params = {
          params: {
            owner_ids: this.mediaOwnerIDs.join(','),
            level: locationLevel,
            unit_targeting: true,
          },
          cancelTokenSource: locationCancelToken,
          showLoader: false,
        };

        const result = await this.getLevelLocationIds(params);

        if (result.length > 0) {
          const batchSize = 250;
          const getBatch = async (page, acc = []) => {
            if (!this.fetchLocations) {
              return [];
            }
            const locationIDs = result.slice(page * batchSize, (page + 1) * batchSize);
            if (locationIDs?.length === 0) {
              return acc;
            }
            const { items } = await this.getLocationsFromIds(locationIDs);
            return getBatch(page + 1, [...acc, ...items]);
          };
          const response = await getBatch(0);
          return response
            .filter(
              (location) =>
                !location.archived ||
                this.locations.some((l) => location.id === l.id) ||
                this.locationExclusions.some((e) => location.id === e.id),
            )
            .sort(this.nameCompare);
        }
        return [];
      },
      async loadCountries() {
        this.cancelLoadCountriesTokenSource = axios.CancelToken.source();
        this.allCountries = await this.loadLocations(0, this.cancelLoadCountriesTokenSource);
        this.searchResultLocationCount = this.allCountries.length;
        this.cancelLoadCountriesTokenSource = null;
      },
      async loadRegions() {
        this.cancelLoadRegionsTokenSource = axios.CancelToken.source();
        this.allRegions = await this.loadLocations(1, this.cancelLoadRegionsTokenSource);
        this.searchResultLocationCount = this.allRegions.length;
        this.cancelLoadRegionsTokenSource = null;
      },
      async loadCities() {
        this.cancelLoadCitiesTokenSource = axios.CancelToken.source();
        this.allCities = await this.loadLocations(3, this.cancelLoadCitiesTokenSource);
        this.searchResultLocationCount = this.allCities.length;
        this.cancelLoadCitiesTokenSource = null;
        this.$emit('cityOptions', this.allCities);
      },
      async loadMarkets() {
        // For initial load
        if (this.entity == null) {
          return;
        }
        // Avoid doing unecessary backend call if the list of available IDs is empty
        if (this.availableMarketIDs.length === 0) {
          this.allMarkets = [];
          this.searchResultMarketCount = 0;
          this.isSearchingMarkets = false;
        }
        this.isSearchingMarkets = true;
        if (this.cancelMarketTreeTokenSource != null) {
          this.cancelMarketTreeTokenSource.cancel();
        }
        const countryIDs = Array.from(
          new Set(Object.values(this.mediaOwnerCountryRoots).reduce((mem, x) => [...mem, ...x], [])),
        );
        this.cancelMarketTreeTokenSource = axios.CancelToken.source();
        try {
          const result = await this.getMarketsWithInventory({
            ownerIDs: this.mediaOwnerIDs,
            countryIDs,
            cancelTokenSource: this.cancelMarketTreeTokenSource,
            showLoader: false,
          });
          if (result != null) {
            this.cancelMarketTreeTokenSource = null;
            this.allMarkets = result.filter((a) => this.availableMarketIDs.includes(a.id)).sort(this.nameCompare);
            this.$emit('marketOptions', this.allMarkets);
            this.searchResultMarketCount = this.allMarkets.length;
          }
        } catch (e) {}
        this.isSearchingMarkets = false;
      },
      async loadAvailableNetworks() {
        // For initial load
        /**
         * This piece of code was moved from the mounted method
         * This is apparently to prevent a useless call to be done for DSPs with no Media Owner
         * @see https://github.com/Perion-Hivestack/hs-frontend/commit/46bd157c1792d9dbee2ae639f3ac28d698758ffb#diff-8e0c054c93da4fd7358f99bf00ee0b8c0b51b850410f0f6d3204ab5a6dfeb114R3244
         */
        const isDemandOrganizationEligibleForNetworks = !(
          this.isSupplyOrganization ||
          this.mediaOwnerIDs.length === 1 ||
          this.organizationSettings?.uiEnableNetworksForHSBuyers
        );
        if (isDemandOrganizationEligibleForNetworks || this.entity == null) {
          return;
        }
        // Avoid doing unecessary backend call if the list of available IDs is empty
        if (this.availableNetworkIDs.length === 0) {
          this.availableNetworks = [];
          this.searchResultNetworkCount = 0;
          this.isSearchingNetworks = false;
          return;
        }

        if (this.networks.length > 0) {
          this.availableNetworks = this.availableNetworkIDs
            .map(
              (id) =>
                this.networks.find((x) => x.id === id) ||
                new Network(id, `${i18n.global.t('messages.unknownID')} (#${id})`),
            )
            .sort(this.nameCompare);

          if (this.isDemandOrganization) {
            this.availableNetworks = this.availableNetworks.map((n) => {
              const newNetwork = Network.fromApi(n.toApi());
              newNetwork.mediaOwnerName = this.getMediaOwnerName(newNetwork.ownerID);
              return newNetwork;
            });
          }

          this.searchResultNetworkCount = this.availableNetworks.length;
          this.showNetworksFilter = this.availableNetworks.length > 0 && !this.isDisplayVideoLineItem;
          this.$emit('networkOptions', this.availableNetworks);

          // Update available filters to get networks filter in the list
          if (this.showNetworksFilter) {
            const ddNetworksFilter = {
              id: 'dd_networks',
              name: capitalize(i18n.global.t('names.network', { n: 2 })),
              isDefault: true,
            };
            this.availableFilters = this.availableFilters.filter((x) => x.id !== 'dd_networks');
            this.availableFilters.push(ddNetworksFilter);

            if (this.isNew) {
              this.selectedFilters = this.availableFilters.filter((x) => x.isDefault);
            } else {
              this.selectedFilters.push(ddNetworksFilter);
            }
          }
        }

        this.isSearchingNetworks = false;
      },
      async loadGeofenceGroups() {
        const getGeofenceGroupsResult = await this.getGeofenceGroups({
          queryParams: QueryParams.all(new FilterCollection([new Filter('usage_id', [GeofenceGroupType.PROXIMITY])])),
          showLoader: false,
        });
        this.geofenceGroups = getGeofenceGroupsResult.items.sort((a, b) => a.name.localeCompare(b.name));
      },
      /**
       * Fetches the list of all the active networks
       */
      async loadActiveNetworks() {
        if (this.mapDataProvider.isPublicPlan) {
          const networkQueryParams = QueryParams.all(new FilterCollection([new Filter('active', [true])]));
          networkQueryParams.password = this.password;
          this.networksPublicQuery = await this.mapDataProvider.getNetworks(networkQueryParams);
          this.activeNetworks = this.networksPublicQuery.items;
        } else {
          this.activeNetworks = this.networks.filter((network) => network.isActive);
        }
        this.colorPerNetworkID = this.activeNetworks.map((n, i) => ({
          id: n.id,
          color: this.networksColorPalette[i % this.networksColorPalette.length],
        }));
      },
      async loadMediaOwners() {
        // Load Media Owners
        if (this.mediaOwners.length === 0) {
          await this.mapDataProvider.getMediaOwners();
        }
      },
      async loadMediaTypes() {
        // Get some reference data
        if (!this.mapDataProvider.isPublicPlan) {
          this.mediaTypes = await runQueryInScope(useMediaTypes);
          this.$emit('mediaTypeOptions', this.mediaTypes);
        }
      },
      async loadExpandedLocations() {
        const loadIncludedLocations = async () => {
          if (this.lineItem?.locationIDs?.length > 0) {
            const getIncludedLocationsResult = await this.getLocationsFromIds(this.lineItem.locationIDs);
            this.locations = getIncludedLocationsResult.items;
          } else {
            this.locations = [];
          }
        };
        const loadExcludedLocations = async () => {
          if (this.lineItem?.locationExclusionIDs?.length > 0) {
            const getExcludedLocationsResult = await this.getLocationsFromIds(this.lineItem.locationExclusionIDs);
            this.locations = getExcludedLocationsResult.items;
          } else {
            this.locationExclusions = [];
          }
        };
        // Can be loaded in independently in parallel
        await Promise.allSettled([loadIncludedLocations(), loadExcludedLocations()]);
      },
      loadCreativeFormats() {
        // for initial load
        this.allCreativeFormats = CreativeFormatOptions.options();
      },
      loadCreativeOrientations() {
        // for initial load
        this.allCreativeOrientations = CreativeOrientationOptions.options();
      },
      loadIabCategories() {
        // for initial load
        this.allIabCategories = IABCategories.hierarchicalOptions();
      },
      async loadAvailableIDsPerFilterType() {
        const params = {
          packageID: this.packageID > 0 ? this.packageID : null,
          showLoader: false,
        };

        if (this.isDemandOrganization && this.lineItem != null) {
          params.packageGroupID = this.lineItem.multiDealsParams.packagegroup_id;
          params.packageIDs = this.lineItem.multiDealsParams.package_ids;
          params.packageID = this.lineItem.packageID;
        }

        const filters = await this.getAvailableIDsPerFilterType(params);
        if (this.canShowUnitPacks && !this.isCuratorOrganization) {
          const unitPackQueryParams = QueryParams.all(
            new FilterCollection([
              new Filter('active', [true], FilterComparisonOperator.EQUAL),
              new Filter('unit_count', [0], FilterComparisonOperator.NOT_EQUAL),
              new Filter('owner_id', this.mediaOwnerIDs, FilterComparisonOperator.IN),
            ]),
          );
          const allUnitPacks = await this.getUnitPacks({ queryParams: unitPackQueryParams });
          this.availableUnitPackIDs = allUnitPacks.items.map((x) => x.id);
        }
        this.availableLocationIDs = filters.location_id;
        this.availableMarketIDs = filters.market_id;
        this.availableMediaTypeIDs = filters.mediatype_id;
        this.availableNetworkIDs = filters.network_id;
      },
      nameCompare(a, b) {
        return a.name.localeCompare(b.name);
      },
      async getLevelLocationIds(params) {
        const fetchAvailableLocations = async () => {
          if (this.availableLocationIDs.length === 0) {
            return [];
          }
          const locationsByLevel = await this.getLocationsByLevel(params);
          return (locationsByLevel || []).filter((a) => {
            return this.availableLocationIDs.includes(a);
          });
        };
        const availableLocations = await fetchAvailableLocations();
        // add location ids already in the line item that might be omitted in the results list because of archived
        const locations = this.locations
          .filter((l) => l.level === params.params.level && !availableLocations.some((r) => r === l.id))
          .map((l) => l.id);
        const locationExclusions = this.locationExclusions
          .filter((l) => l.level === params.params.level && !availableLocations.some((r) => r === l.id))
          .map((l) => l.id);
        return [...availableLocations, ...locations, ...locationExclusions];
      },
      async getLocationsFromIds(locationIds, getShapes = false) {
        const locationQP = QueryParams.for({
          top: null,
          include: ['location_id', 'level', 'code', 'name', 'hierarchy', 'archived'],
          filterCollection: new FilterCollection([new Filter('location_id', locationIds, FilterComparisonOperator.IN)]),
        });

        if (getShapes) {
          locationQP.expand = ['shapes'];
          locationQP.include.push('shapes');
        }

        return this.getLocations({ queryParams: locationQP, showLoader: false });
      },
      handleUnitListScroll(e) {
        if (
          this.lastUnitSearchQueryParams != null &&
          this.unitPageInCurrentSearch * this.searchResultPageSize < this.searchResultUnitCount
        ) {
          if (e.target.scrollTop + e.target.offsetHeight > e.target.scrollHeight - 100) {
            this.nextUnitSearchResultPage();
          }
        }
      },
      async fetchUnits(queryParams) {
        return this.mapDataProvider.getUnits(queryParams);
      },
      async fetchSites(queryParams) {
        return this.mapDataProvider.getSites(queryParams);
      },
      async getFilteredLocationsInternal(fitToBounds = false) {
        if (this.organizationSettings?.uiEnableSelectTargetingToSeeScreens && !this.hasStartedFiltering) {
          this.cart?.clear();
          if (!this.isFirstClusterLoad) {
            this.clusters = null;
            this.map?.defaultZoom();
          }
          return;
        }
        this.isLoadingUnits = true;
        this.isMapUpdated = true;

        // Delay until map is ready.
        await waitUntil(() => this.map != null && this.mapDataProvider != null);

        const params = {};

        const networks = this.filters.filter((x) => MapCart.isNetwork(x)) || [...this.activeNetworks];
        if (this.filters.filter((x) => x.exchangeID != null).length > 0) {
          params.network_ids = networks.map((x) => x.id);
        }

        if (this.unitPack != null || this.network != null) {
          const unitIDs = this.updateIndexForUnitPack > 1 ? this.cart.includedUnitIDs : this.unitIDs;
          params.unit_ids = [...new Set(unitIDs)];
        } else {
          params.unit_ids = this.cart.includedUnitIDs || [];
        }
        params.owner_ids = this.mediaOwnerIDs.length > 0 ? this.mediaOwnerIDs : undefined; // BE will not return result if owner_ids is an empty array.
        params.location_ids = this.cart.includedLocationIDs;
        params.location_exclusion_ids = this.cart.excludedLocationIDs;
        params.market_ids = this.cart.includedMarketIDs || [];
        params.market_exclusion_ids = this.cart.excludedMarketIDs || [];
        params.network_ids = this.cart.includedNetworkIDs || [];
        params.network_exclusion_ids = this.cart.excludedNetworkIDs || [];
        params.site_ids = this.cart.includedSiteIDs || [];
        params.site_exclusion_ids = this.cart.excludedSiteIDs || [];
        params.unit_exclusion_ids = this.cart.excludedUnitIDs || [];
        params.mediatype_ids = this.cart.includedMediaTypeIDs || [];
        params.mediatype_exclusion_ids = this.cart.excludedMediaTypeIDs || [];
        params.geofencegroup_ids = this.cart.includedGeofenceGroupIDs || [];
        params.geofencegroup_exclusion_ids = this.cart.excludedGeofenceGroupIDs || [];
        params.unit_tags = this.cart.includedTags.map((x) => x.name);
        params.unit_tag_exclusions = this.cart.excludedTags.map((x) => x.name);
        params.package_id = this.packageID || null;
        params.creative_formats = this.cart.includedCreativeFormatsIDs || [];
        params.creative_format_exclusions = this.cart.excludedCreativeFormatsIDs || [];
        params.creative_orientations = this.cart.includedCreativeOrientationIDs || [];
        params.creative_orientation_exclusions = this.cart.excludedCreativeOrientationIDs || [];
        params.iab_categories = this.cart.includedIabCategoryIDs || [];
        params.iab_category_exclusions = this.cart.excludedIabCategoryIDs || [];
        params.unique_advertiser_id_to_check = this.advertiser?.uniqueAdvertiser?.id || null;
        params.unitpack_ids = this.cart.includedUnitPackIDs || [];
        params.unitpack_exclusion_ids = this.cart.excludedUnitPackIDs || [];
        params.ignore_buy_type_restrictions = this.ignoreBuyTypeRestrictions;
        params.language_ids = this.cart.includedLanguageIds || [];
        params.language_exclusion_ids = this.cart.excludedLanguageIds || [];

        if (this.isDemandOrganization && this.lineItem != null) {
          params.packagegroup_id = this.lineItem.multiDealsParams.packagegroup_id;
          params.package_ids = this.lineItem.multiDealsParams.package_ids;
          params.buy_from_deals_enabled = this.lineItem.buyFromDealsEnabled;
          params.package_id = this.lineItem.packageID;
        }

        if (this.isCuratorOrganization) {
          params.package_ids = this.lineItem.packageIDs;
        }

        if (this.showNewUI) {
          params.type = 'plans';
        }

        // Required for BE to restrict buyer-supplier links based on country (CAM-5104)
        params.requested_for_utc = toServerDateTime(this.requestedForUTC);

        // When editing a Deal, we need to send -1, otherwise BE returns the previous targeting values already saved on the package
        if (this.isEditing && this.lineItem instanceof Package) {
          params.package_id = -1;
        }

        this.params = params;

        if (this.getClusteredUnitsFromTargetsCancelTokenSource != null) {
          this.getClusteredUnitsFromTargetsCancelTokenSource.cancel();
        }
        this.getClusteredUnitsFromTargetsCancelTokenSource = axios.CancelToken.source();

        const mapBounds = this.map?.getBounds();

        //
        // Get a padded bounding box.
        //
        const bbox =
          mapBounds.length === 0 ||
          (this.isFirstClusterLoad && this.geofenceGroupPolygonFeatures?.length === 0) ||
          fitToBounds
            ? [-180, -90, 180, 90]
            : mapBounds;
        const poly = turfBboxPolygon(bbox);
        const scaledPoly = turfTransformScale(poly, 1.2);
        let scaledBbox = turfBbox(scaledPoly);

        scaledBbox = [
          Math.max(Math.min(bbox[0], scaledBbox[0]), -180),
          Math.max(Math.min(bbox[1], scaledBbox[1]), -90),
          Math.min(Math.max(bbox[2], scaledBbox[2]), 180),
          Math.min(Math.max(bbox[3], scaledBbox[3]), 90),
        ];

        this.zoomLevel = this.map.getZoom();
        if (!this.isDisplayVideoLineItem) {
          if (this.showNetworks) {
            this.clusters = await this.mapDataProvider.getClusteredSiteNetworksFromTargets({
              params,
              minLat: scaledBbox[1],
              maxLat: scaledBbox[3],
              minLng: scaledBbox[0],
              maxLng: scaledBbox[2],
              zoom: this.isFirstClusterLoad ? 1 : Math.max(1, Math.round(this.$refs.mapboxMap.getZoom() - 1)),
              cancelTokenSource: this.getClusteredUnitsFromTargetsCancelTokenSource,
              showLoader: false,
              type: this.clusteredType,
            });
          } else {
            this.clusters = await this.mapDataProvider.getClusteredUnitsFromTargets({
              params,
              minLat: scaledBbox[1],
              maxLat: scaledBbox[3],
              minLng: scaledBbox[0],
              maxLng: scaledBbox[2],
              zoom: this.isFirstClusterLoad || fitToBounds ? 1 : Math.max(1, Math.round(this.zoomLevel)),
              cancelTokenSource: this.getClusteredUnitsFromTargetsCancelTokenSource,
              showLoader: false,
              type: this.clusteredType,
            });
          }
        } else {
          this.clusters = null;
          this.isFirstClusterLoad = false;
          this.isLoadingUnits = false;
        }

        if (this.clusters != null) {
          this.updateIndexForUnitPack += 1;

          await delay(500);
          this.isLoadingUnits = false;

          if ((this.isFirstClusterLoad || fitToBounds) && this.clusters?.length > 0) {
            const bounds = this.clusters.reduce(
              (bounds, x) => {
                return [
                  Math.min(x.bounds.min_lon, bounds[0]),
                  Math.min(x.bounds.min_lat, bounds[1]),
                  Math.max(x.bounds.max_lon, bounds[2]),
                  Math.max(x.bounds.max_lat, bounds[3]),
                ];
              },
              [180, 90, -180, -90],
            );

            this.defaultBounds = bounds;
            this.map.fitBounds([
              [bounds[0], bounds[1]],
              [bounds[2], bounds[3]],
            ]);
            this.isFirstClusterLoad = false;
          }
        }
      },
      async getFilteredLocations(fitToBounds = false) {
        if (this.getFilteredLocationsThrottled == null) {
          this.getFilteredLocationsThrottled = new ThrottledTaskRunner(
            (fit = false) => this.getFilteredLocationsInternal(fit),
            200,
          );
        }
        return this.getFilteredLocationsThrottled.run(fitToBounds);
      },
      async initCart(
        cartUnitIDs = [],
        cartUnitExclusionIDs = [],
        cartSiteIDs = [],
        cartSiteExclusionIDs = [],
        cartLocations = [],
        cartLocationExclusions = [],
        cartMarketIDs = [],
        cartMarketExclusionIDs = [],
        cartGeofenceGroupIDs = [],
        cartGeofenceGroupExclusionIDs = [],
        cartMediaTypeIDs = [],
        cartMediaTypeExclusionIDs = [],
        cartNetworkIDs = [],
        cartNetworkExclusionIDs = [],
        cartUnitTags = [],
        cartUnitTagExclusions = [],
        cartUnitPackIDs = [],
        cartUnitPackExclusionIDs = [],
        cartCreativeFormats = [],
        cartCreativeFormatExclusions = [],
        cartLanguageIDs = [],
        cartLanguageExclusionIDs = [],
        cartCreativeOrientations = [],
        cartCreativeOrientationExclusions = [],
        cartIabCategories = [],
        cartIabCategoryExclusions = [],
      ) {
        this.cart.isInitialized = false;
        this.cart.clear();
        this.cart.autoUpdateSessionCart = false;
        const promises = [];

        //
        // Restore tag selections
        //
        const allTags = [...cartUnitTagExclusions, ...cartUnitTags];
        if (allTags.length > 0 && !this.isSidekickOrganization) {
          let result = this.allTags;
          if (result == null || result?.length === 0) {
            const params = {
              params: {
                owner_ids: this.mediaOwnerIDs,
                unit_targeting: true,
              },
              cancelTokenSource: axios.CancelToken.source(),
              showLoader: false,
            };
            result = (await this.getTags(params))?.map((x, i) => UnitTag.fromApi(x, i));
          }
          if (result != null) {
            const includedTags = result.filter((x) => cartUnitTags.includes(x.tag));
            const excludedTags = result.filter((x) => cartUnitTagExclusions.includes(x.tag));
            this.updateUnitTags(includedTags.map((x) => x.tag));
            this.updateUnitTagExclusions(excludedTags.map((x) => x.tag));
            this.cart.add(includedTags);
            this.cart.add(excludedTags, true);
            if (this.autoApplyCartToFilters) {
              includedTags.forEach((x) => this.addFilter(x, false));
            }
          }
        }

        //
        // Restore unit selections
        //
        const allCartUnitIDs = [...cartUnitIDs, ...cartUnitExclusionIDs];
        if (allCartUnitIDs.length > 0) {
          this.onScreenIdsImported(cartUnitIDs, cartUnitExclusionIDs, false);
        }

        //
        // Restore site selections
        //
        const allCartSiteIDs = [...cartSiteIDs, ...cartSiteExclusionIDs];
        if (allCartSiteIDs.length > 0) {
          const siteQP = QueryParams.for({
            top: null,
            filterCollection: new FilterCollection([
              new Filter('site_id', allCartSiteIDs, FilterComparisonOperator.IN),
            ]),
          });
          const result = await this.getSites({
            queryParams: siteQP,
            showLoader: false,
          });
          const sites = result.items;
          // Safe guard to not end up calling the API too many times
          if (sites.length < 10) {
            sites.forEach((x) => x.checkStreetViewImageAvailability());
          }
          sites.forEach((site) => site.units.forEach((unit) => this.updateUnitSite({ unit, value: site })));
          this.cart.add(sites.filter((x) => cartSiteIDs.find((y) => x.id === y) != null));
          this.cart.add(
            sites.filter((x) => cartSiteExclusionIDs.find((y) => x.id === y) != null),
            true,
          );
          if (this.autoApplyCartToFilters) {
            sites.forEach((x) => this.addFilter(x, false));
          }
          this.onSiteIDsImported(cartSiteIDs, cartSiteExclusionIDs, false);
        }

        //
        // Restore market selections
        //
        if (cartMarketIDs != null && cartMarketIDs.length > 0) {
          const marketQP = QueryParams.for({
            top: null,
            filterCollection: new FilterCollection([
              new Filter('market_id', cartMarketIDs, FilterComparisonOperator.IN),
            ]),
          });
          const marketResult = await this.getMarkets({
            queryParams: marketQP,
            showLoader: false,
          });
          const markets = marketResult.items;
          this.cart.add(markets);
          if (this.autoApplyCartToFilters) {
            markets.forEach((x) => this.addFilter(x, false));
          }
        }

        // Restore excluded market selections
        if (cartMarketExclusionIDs.length > 0) {
          const marketQP = QueryParams.for({
            top: null,
            filterCollection: new FilterCollection([
              new Filter('market_id', cartMarketExclusionIDs, FilterComparisonOperator.IN),
            ]),
          });
          const marketResult = await this.getMarkets({
            queryParams: marketQP,
            showLoader: false,
          });
          const excludedMarkets = marketResult.items;
          this.cart.add(
            excludedMarkets.filter((x) => cartMarketExclusionIDs.find((y) => x.id === y) != null),
            true,
          );
          if (this.autoApplyCartToFilters) {
            excludedMarkets.forEach((x) => this.addFilter(x, false));
          }
        }

        //
        // Restore network selections
        //
        if (cartNetworkIDs?.length > 0 || cartNetworkExclusionIDs?.length > 0) {
          if (cartNetworkIDs?.length > 0) {
            const includedNetworks = cartNetworkIDs.map(
              (id) =>
                this.networks.find((x) => x.id === id) ||
                new Network(id, `${i18n.global.t('messages.unknownID')} (#${id})`, null, true),
            );
            this.cart.add(includedNetworks);
            if (this.autoApplyCartToFilters) {
              includedNetworks.forEach((x) => this.addFilter(x, false));
            }
          }
          if (cartNetworkExclusionIDs?.length > 0) {
            const excludedNetworks = cartNetworkExclusionIDs.map(
              (id) =>
                this.networks.find((x) => x.id === id) ||
                new Network(id, `${i18n.global.t('messages.unknownID')} (#${id})`, null, true),
            );
            this.cart.add(
              excludedNetworks.filter((x) => cartNetworkExclusionIDs.find((y) => x.id === y) != null),
              true,
            );
            if (this.autoApplyCartToFilters) {
              excludedNetworks.forEach((x) => this.addFilter(x, false));
            }
          }
        }

        //
        // Restore media type selections
        //
        if (cartMediaTypeIDs?.length > 0 || cartMediaTypeExclusionIDs?.length > 0) {
          const mediaTypesResult = await runQueryInScope(useMediaTypes);
          const getAllMediaTypes = (mem, x) => [
            ...mem,
            x,
            ...(x.children.length > 0 ? x.children.reduce(getAllMediaTypes, []) : []),
          ];
          const mediaTypes = mediaTypesResult.reduce(getAllMediaTypes, []);

          if (cartMediaTypeIDs?.length > 0) {
            const includedMediaTypes = cartMediaTypeIDs.map(
              (id) =>
                mediaTypes.find((x) => x.id === id) ||
                new MediaType(id, `${i18n.global.t('messages.unknownID')} (#${id})`),
            );
            this.cart.add(includedMediaTypes);
            if (this.autoApplyCartToFilters) {
              includedMediaTypes.forEach((x) => this.addFilter(x, false));
            }
          }

          if (cartMediaTypeExclusionIDs?.length > 0) {
            const excludedMediaTypes = cartMediaTypeExclusionIDs.map(
              (id) =>
                mediaTypes.find((x) => x.id === id) ||
                new MediaType(id, `${i18n.global.t('messages.unknownID')} (#${id})`),
            );
            this.cart.add(excludedMediaTypes, true);
            if (this.autoApplyCartToFilters) {
              excludedMediaTypes.forEach((x) => this.addFilter(x, false));
            }
          }
        }
        this.areMediaTypesDataReady = true;

        //
        // Restore location selections
        //
        const locationIDs =
          cartLocations != null
            ? [
                ...cartLocations.map((x) => x.id),
                ...(cartLocationExclusions != null ? cartLocationExclusions.map((x) => x.id) : []),
              ]
            : [];
        if (locationIDs.length > 0) {
          this.cart.isInitialized = true;
          this.cart.add(cartLocations);
          if (cartLocationExclusions != null && cartLocationExclusions.length > 0) {
            this.cart.add(cartLocationExclusions, true);
          }
          const locationQP = QueryParams.for({
            top: null,
            filterCollection: new FilterCollection([
              new Filter('location_id', locationIDs, FilterComparisonOperator.IN),
            ]),
            expand: ['shapes'],
          });
          const p = this.getLocations({ queryParams: locationQP }).then((result) => {
            if (result?.items) {
              this.locationsWithShapesInCart = result.items;
              if (this.autoApplyCartToFilters) {
                this.locationsWithShapesInCart.forEach((x) => this.addFilter(x, false));
              }
            }
            if (this.map?.updateLocationPolygonsDatasource != null) {
              this.map.updateLocationPolygonsDatasource();
            }
          });
          promises.push(p);
        }

        /**
         * Restore geofence group selections
         */
        const geofencePromise = this.loadGeofenceSelections(cartGeofenceGroupIDs, cartGeofenceGroupExclusionIDs);
        promises.push(geofencePromise);

        //
        // Restore unitPack selections
        //
        const allCartUnitPackIDs = [...cartUnitPackIDs, ...cartUnitPackExclusionIDs];
        if (this.canShowUnitPacks && allCartUnitPackIDs.length > 0) {
          const val = { included: [], excluded: [] };
          const unitPackQP = QueryParams.for({
            top: null,
            filterCollection: new FilterCollection([
              new Filter('unitpack_id', allCartUnitPackIDs, FilterComparisonOperator.IN),
            ]),
          });
          const result = await this.getUnitPacks({
            queryParams: unitPackQP,
            showLoader: false,
          });
          const unitpacks = result.items;
          if (unitpacks.length) {
            val.included = unitpacks.filter((x) => cartUnitPackIDs.find((y) => x.id === y) != null);
            val.excluded = unitpacks.filter((x) => cartUnitPackExclusionIDs.find((y) => x.id === y) != null);
            this.updateUnitPacks(val.included.map((x) => x.id));
            this.updateUnitPackExclusions(val.excluded.map((x) => x.id));
            this.cart.add(val.included);
            this.cart.add(val.excluded, true);
          }
          if (this.autoApplyCartToFilters) {
            unitpacks.forEach((x) => this.addFilter(x, false));
          }

          this.unitPacksForFilters = val.included;

          if (this.$refs.dd_unitpacks) {
            this.$refs.dd_unitpacks.initSelections(true, val.included, val.excluded);
          }
          this.onUnitPackSelectionChanged(val);
        }

        //
        // Restore creative formats selections
        //
        const allCreativeFormats = [...cartCreativeFormatExclusions, ...cartCreativeFormats];
        if (allCreativeFormats.length > 0) {
          const includedCreativeFormats = allCreativeFormats
            .filter((t) => cartCreativeFormats.indexOf(t) > -1)
            .map((t) => ({ id: t }));
          const excludedCreativeFormats = allCreativeFormats
            .filter((t) => cartCreativeFormatExclusions.indexOf(t) > -1)
            .map((t) => ({ id: t }));
          this.updateCreativeFormats(includedCreativeFormats.map((x) => x.id));
          this.updateCreativeFormatExclusions(excludedCreativeFormats.map((x) => x.id));
          this.cart.add(includedCreativeFormats);
          this.cart.add(excludedCreativeFormats, true);
          if (this.autoApplyCartToFilters) {
            includedCreativeFormats.forEach((x) => this.addFilter(x, false));
          }
        }

        // Restore language selections
        if (!this.creative && (cartLanguageIDs?.length > 0 || cartLanguageExclusionIDs?.length > 0)) {
          await this.loadLanguages();

          if (cartLanguageExclusionIDs?.length > 0) {
            const excludedLanguages = cartLanguageExclusionIDs.map(
              (id) =>
                this.languages.find((x) => x.id === id) ||
                new Language(id, `${i18n.global.t('messages.unknownID')} (#${id})`),
            );
            this.cart.add(excludedLanguages, true);
            if (this.autoApplyCartToFilters) {
              excludedLanguages.forEach((x) => this.addFilter(x, false));
            }
          }

          if (cartLanguageIDs?.length > 0) {
            const includedLanguages = cartLanguageIDs.map(
              (id) =>
                this.languages.find((x) => x.id === id) ||
                new Language(id, `${i18n.global.t('messages.unknownID')} (#${id})`),
            );
            this.cart.add(includedLanguages);
            if (this.autoApplyCartToFilters) {
              includedLanguages.forEach((x) => this.addFilter(x, false));
            }
          }
        }

        //
        // Restore creative orientations selections
        //
        const allCreativeOrientations = [...cartCreativeOrientationExclusions, ...cartCreativeOrientations];
        if (allCreativeOrientations.length > 0) {
          const includedCreativeOrientations = allCreativeOrientations
            .filter((t) => cartCreativeOrientations.indexOf(t) > -1)
            .map((t) => ({ id: t }));
          const excludedCreativeOrientations = allCreativeOrientations
            .filter((t) => cartCreativeOrientationExclusions.indexOf(t) > -1)
            .map((t) => ({ id: t }));
          this.updateCreativeOrientations(includedCreativeOrientations.map((x) => x.id));
          this.updateCreativeOrientationExclusions(excludedCreativeOrientations.map((x) => x.id));
          this.cart.add(includedCreativeOrientations);
          this.cart.add(excludedCreativeOrientations, true);
          if (this.autoApplyCartToFilters) {
            includedCreativeOrientations.forEach((x) => this.addFilter(x, false));
          }
        }

        //
        // Restore IAB categories selections
        //
        if (!this.creative && (cartIabCategories?.length > 0 || cartIabCategoryExclusions?.length > 0)) {
          const allIabCategories = [...IABCategories.hierarchicalOptions(), ...IABCategories.subCategoriesOptions()];
          if (cartIabCategoryExclusions?.length > 0) {
            const excludedIabCategories = allIabCategories.filter((x) => cartIabCategoryExclusions.indexOf(x.id) > -1);
            this.cart.add(excludedIabCategories, true);
            if (this.autoApplyCartToFilters) {
              excludedIabCategories.forEach((x) => this.addFilter(x, false));
            }
          }

          if (cartIabCategories?.length > 0) {
            const includedIabCategories = allIabCategories.filter((x) => cartIabCategories.indexOf(x.id) > -1);
            this.cart.add(includedIabCategories);
            if (this.autoApplyCartToFilters) {
              includedIabCategories.forEach((x) => this.addFilter(x, false));
            }
          }
        }

        this.isFiltersRestored = true;
        // When there are close to 5000 units, map is frozen for few seconds before updating targeted units.

        if (this.unitPack != null || this.network != null) {
          this.$emit('unitsLoading');
        }
        await Promise.all(promises);
        if (this.unitPack != null || this.network != null) {
          this.$emit('unitsLoaded');
        }

        if (!this.isMapUpdated) {
          this.updateMapAccordingToFilters(true);
        }

        this.cart.autoUpdateSessionCart = true;
        this.cart.isInitialized = true;
        this.isReady = true;
      },
      async loadGeofenceSelections(cartGeofenceGroupIDs, cartGeofenceGroupExclusionIDs) {
        const promises = [];

        const processGeofenceGroups = async (ids, isExclusion) => {
          if (ids.length === 0) return;

          const geofenceGroupQP = QueryParams.for({
            top: null,
            filterCollection: new FilterCollection([new Filter('geofencegroup_id', ids, FilterComparisonOperator.IN)]),
          });

          let result;
          if (this.mapDataProvider.isPublicPlan && this.sharedPlanMapUUID) {
            geofenceGroupQP.customQueryParams = { uuid: this.sharedPlanMapUUID };
            result = await this.getPublicPlanGeofenceGroups({ queryParams: geofenceGroupQP });
          } else {
            geofenceGroupQP.expand = ['geofences'];
            result = await this.getGeofenceGroups({ queryParams: geofenceGroupQP });
          }

          const geofenceGroups = result.items;
          if (geofenceGroups.length !== ids.length) {
            this.cart.add(
              [GeofenceGroup.fromObject({ name: capitalize(i18n.global.t('names.geofenceGroupNoDetails')) })],
              isExclusion,
            );
          }

          this.cart.add(geofenceGroups, isExclusion);
          if (this.autoApplyCartToFilters) {
            geofenceGroups.forEach((x) => this.addFilter(x, false));
          }
        };

        // Restore geofence group selections and exclusions
        promises.push(processGeofenceGroups(cartGeofenceGroupIDs, false));
        promises.push(processGeofenceGroups(cartGeofenceGroupExclusionIDs, true));

        if (this.geofenceGroupForDrawnSelections && !this.mapDataProvider.isPublicPlan) {
          this.cart.add(this.geofenceGroupForDrawnSelections);
          if (this.autoApplyCartToFilters) {
            this.addFilter(this.geofenceGroupForDrawnSelections, false);
          }
        }

        return Promise.all(promises);
      },
      onMarketSelectionChanged(val) {
        if (this.allMarkets == null) {
          return;
        }
        this.removeFromCart(this.cart.marketsInCart);
        this.addToCart(val.included);
        this.addToCart(val.excluded, true);
        this.$emit('filterSelectionChanged', 'Market');
      },
      onMarketsSelectionsCleared() {
        this.removeFromCart(this.cart.marketsInCart);
      },
      onNetworkSelectionChanged(val) {
        if (this.availableNetworks == null) {
          return;
        }
        this.removeFromCart(this.cart.networksInCart);
        this.addToCart(val.included);
        this.addToCart(val.excluded, true);
        this.$emit('filterSelectionChanged', 'Network');
      },
      onNetworksSelectionsCleared() {
        this.removeFromCart(this.cart.networksInCart);
      },
      async siteIDsImported(siteIDs, selectedUploadOption) {
        this.selectedSiteUploadOption = selectedUploadOption;
        if (this.selectedSiteUploadOption === 'include') {
          this.onSiteIDsImported(siteIDs);
        } else {
          this.onSiteIDsImported(this.entity.siteIDs, siteIDs);
        }
      },
      async onSiteIDsImported(siteIDs, excludedSiteIDs = null, useAsExternalIDs = true) {
        const val = {};

        const batchSize = 500;
        const allSiteIDs = [...siteIDs, ...(excludedSiteIDs == null ? [] : excludedSiteIDs)];
        const uniqueSiteIDs = [...new Set(allSiteIDs)];
        const siteIdBatches = uniqueSiteIDs.reduce((mem, x, i) => {
          const batchIndex = Math.floor(i / batchSize);
          if (mem[batchIndex] == null) {
            mem[batchIndex] = [x];
          } else {
            mem[batchIndex].push(x);
          }
          return mem;
        }, []);
        const queries = siteIdBatches.map((batch) =>
          QueryParams.all(
            FilterCollection.for(useAsExternalIDs ? 'external_id' : 'site_id', batch, FilterComparisonOperator.IN),
            ['site_id', 'external_id', 'name'],
          ),
        );

        let result = [];
        GlobalEventBus.$emit(GlobalEvents.enableLoader);
        GlobalEventBus.$emit(GlobalEvents.loading);
        try {
          const results = await Promise.all(queries.map((queryParams) => this.getSites({ queryParams })));
          result = results?.map((data) => data.items)?.flat() ?? [];
        } catch (e) {
          // error
        } finally {
          GlobalEventBus.$emit(GlobalEvents.loaded);
          GlobalEventBus.$emit(GlobalEvents.disableLoader);
        }

        val.included = result.filter((x) => siteIDs.some((v) => v === (useAsExternalIDs ? x.externalID : x.id)));
        val.excluded =
          excludedSiteIDs == null
            ? []
            : result.filter((x) => excludedSiteIDs.some((v) => v === (useAsExternalIDs ? x.externalID : x.id)));

        // When imported ID's doesn't match any sites
        if (
          (this.selectedSiteUploadOption === 'include' && val.included === 0) ||
          (this.selectedSiteUploadOption === 'exclude' && val.excluded === 0)
        ) {
          this.missingSiteIDs = siteIDs;
          this.isMissingSiteIDsModalVisible = true;
          return;
        }
        if (this.selectedSiteUploadOption === 'include' && useAsExternalIDs && siteIDs.length !== val.included.length) {
          this.missingSiteIDs = siteIDs.filter((x) => !val.included.some((s) => s.externalID === x));
          this.isMissingSiteIDsModalVisible = true;
        } else if (
          this.selectedSiteUploadOption === 'exclude' &&
          useAsExternalIDs &&
          excludedSiteIDs.length !== val.excluded.length
        ) {
          this.missingSiteIDs = excludedSiteIDs.filter((x) => !val.excluded.some((s) => s.externalID === x));
          this.isMissingSiteIDsModalVisible = true;
        }
        this.sitesForFilters = val.included;

        if (this.$refs.dd_sites != null) {
          this.$refs.dd_sites.initSelections(true, val.included, val.excluded);
        }
      },
      onSiteSelectionChanged(val) {
        if (this.sitesForFilters == null) {
          return;
        }
        this.removeFromCart(this.cart.sitesInCart);
        this.addToCart(val.included);
        this.addToCart(val.excluded, true);
        this.$emit('filterSelectionChanged', 'Site');
      },
      onSitesSelectionsCleared() {
        this.removeFromCart(this.cart.sitesInCart);
      },
      async screenIDsImported(screenIDs, selectedUploadOption, filterScreensByExternalID) {
        if (screenIDs.length > this.importedUnitsMax) {
          this.showMaxScreenUploadWarningModal = true;
          this.isLoadingUnits = false;
          return;
        }
        this.selectedScreenUploadOption = selectedUploadOption;
        if (this.selectedScreenUploadOption === 'include') {
          await this.onScreenIdsImported(screenIDs, null, filterScreensByExternalID);
        } else {
          await this.onScreenIdsImported(null, screenIDs, filterScreensByExternalID);
        }
      },
      /**
       * @param unitIDs {string[]}
       * @param excludedUnitIDs {string[]}
       * @param useAsExternalIDs {boolean}
       * @param units {Unit[]}
       * @returns {Promise<void>}
       */
      async onScreenIdsImported(
        unitIDs,
        excludedUnitIDs = null,
        useAsExternalIDs = true,
        units = null, // If we already have fetched the list of units, pass it here.
      ) {
        const val = {};

        unitIDs = [...new Set(unitIDs)]; // unique ids
        excludedUnitIDs = [...new Set(excludedUnitIDs)]; // unique ids

        if (units == null) {
          // Units have not been fetched yet.
          const batchSize = 1000;

          const getBatchUnits = async (unitIDsArray) => {
            if (!unitIDsArray) {
              unitIDsArray = [];
            }
            const unitIdBatches = unitIDsArray.reduce((mem, x, i) => {
              const batchIndex = Math.floor(i / batchSize);
              if (mem[batchIndex] == null) {
                mem[batchIndex] = [x];
              } else {
                mem[batchIndex].push(x);
              }
              return mem;
            }, []);
            const queries = unitIdBatches.map((batch) =>
              QueryParams.all(
                new FilterCollection([
                  new Filter(useAsExternalIDs ? 'external_id' : 'unit_id', batch, FilterComparisonOperator.IN),
                ]),
                ['unit_id', 'external_id', 'name', 'active'],
              ),
            );

            const promiseResults = await Promise.all(
              queries.map((queryParams) => this.getUnits({ queryParams, showLoader: false })),
            );

            return promiseResults?.map((data) => data.items)?.flat() ?? [];
          };

          let includedUnits = [];
          let excludedUnits = [];
          GlobalEventBus.$emit(GlobalEvents.enableLoader);
          GlobalEventBus.$emit(GlobalEvents.loading);
          try {
            includedUnits = await getBatchUnits(unitIDs);
            excludedUnits = await getBatchUnits(excludedUnitIDs);
          } catch (e) {
            // error
          } finally {
            GlobalEventBus.$emit(GlobalEvents.loaded);
            GlobalEventBus.$emit(GlobalEvents.disableLoader);
          }

          this.addToCart(includedUnits);
          this.addToCart(excludedUnits, true);

          val.included = includedUnits;
          val.excluded = excludedUnits;
        }
        // When imported ID's doesn't match any screen
        if (
          (this.selectedScreenUploadOption === 'include' && val.included === 0) ||
          (this.selectedScreenUploadOption === 'exclude' && val.excluded === 0)
        ) {
          this.missingScreenIDs = unitIDs;
          this.isMissingScreenIDsModalVisible = true;
          return;
        }
        if (this.selectedScreenUploadOption === 'include' && unitIDs.length > val.included.length) {
          let missingUnits = [];
          if (useAsExternalIDs) {
            missingUnits = unitIDs.filter((x) => !val.included.some((s) => s.externalID == x));
          } else {
            missingUnits = unitIDs.filter((x) => !val.included.some((s) => s.id == x));
          }
          this.missingScreenIDs = missingUnits;
          this.isMissingScreenIDsModalVisible = true;
        } else if (this.selectedScreenUploadOption === 'exclude' && excludedUnitIDs.length > val.excluded.length) {
          let missingUnits = [];
          if (useAsExternalIDs) {
            missingUnits = excludedUnitIDs.filter((x) => !val.excluded.some((s) => s.externalID == x));
          } else {
            missingUnits = excludedUnitIDs.filter((x) => !val.excluded.some((s) => s.id == x));
          }
          this.missingScreenIDs = missingUnits;
          this.isMissingScreenIDsModalVisible = true;
        }

        this.unitsForFilters = val.included;

        if (this.cart.includedUnitIDs?.length || this.cart.excludedUnitIDs?.length) {
          await waitUntil(() => this.$refs.dd_units != null);
          this.$refs.dd_units.initSelections(true, val.included, val.excluded);
        }

        GlobalEventBus.$emit(GlobalEvents.loaded);
      },
      onUnitsSelectionsCleared() {
        this.cart.removeAll(this.cart.unitsInCart);
      },
      onUnitSelectionChanged(val) {
        if (this.unitsForFilters == null) {
          return;
        }
        // unit packs only support screen ids
        if (this.unitPack != null || this.network != null) {
          let unitIdsForUnitPack = [];
          if (val.included.length > 0) {
            unitIdsForUnitPack = val.included.map((x) => x.id);
          }
          this.$emit('onMapFilterSelectionChanged', unitIdsForUnitPack);
        }
        this.removeFromCart(this.cart.unitsInCart, false, true);
        this.addToCart(val.included);
        this.addToCart(val.excluded, true);
        this.$emit('filterSelectionChanged', 'Unit');
      },
      onUnitPackSelectionChanged(val) {
        if (this.unitPacksForFilters == null) {
          return;
        }
        this.removeFromCart(this.cart.unitPacksInCart);
        this.addToCart(val.included);
        this.addToCart(val.excluded, true);
        this.$emit('filterSelectionChanged', 'Unit Pack');
      },
      onUnitPacksSelectionsCleared() {
        this.removeFromCart(this.cart.unitPacksInCart);
      },
      onRegionSelectionChanged(val) {
        if (this.allRegions == null) {
          return;
        }
        this.removeFromCart(this.cart.regionsInCart);
        this.addToCart(val.included);
        this.addToCart(val.excluded, true);
        this.$emit('filterSelectionChanged', 'Region');
      },
      onRegionsSelectionsCleared() {
        this.removeFromCart(this.cart.regionsInCart);
      },
      onCountriesSelectionChanged(val) {
        if (this.allCountries == null) {
          return;
        }
        this.removeFromCart(this.cart.countriesInCart);
        this.addToCart(val.included);
        this.addToCart(val.excluded, true);
        this.$emit('filterSelectionChanged', 'Countries');
      },
      onCountriesSelectionsCleared() {
        this.removeFromCart(this.cart.countriesInCart);
      },
      onLanguagesSelectionChanged(val) {
        if (!this.languages) {
          return;
        }
        this.removeFromCart(this.cart.languagesInCart);
        this.addToCart(val.included);
        this.addToCart(val.excluded, true);
        this.$emit('filterSelectionChanged', 'Languages');
      },
      onLanguagesSelectionsCleared() {
        this.removeFromCart(this.cart.languagesInCart);
      },
      onTagsSelectionChanged(val) {
        if (this.allTags == null) {
          return;
        }
        this.removeFromCart(this.cart.tagsInCart);
        this.addToCart(val.included);
        this.addToCart(val.excluded, true);
        this.$emit('filterSelectionChanged', 'Tags');
      },
      onTagsSelectionsCleared() {
        this.removeFromCart(this.cart.tagsInCart);
      },
      onCitySelectionChanged(val) {
        if (this.allCities == null) {
          return;
        }
        this.removeFromCart(this.cart.citiesInCart);
        this.addToCart(val.included);
        this.addToCart(val.excluded, true);
        this.$emit('filterSelectionChanged', 'City');
      },
      onCitySelectionCleared() {
        this.removeFromCart(this.cart.citiesInCart);
      },
      onCreativeFormatsSelectionChanged(val) {
        if (this.allCreativeFormats == null) {
          return;
        }
        this.removeFromCart(this.cart.creativeFormatsInCart);
        this.addToCart(val.included);
        this.addToCart(val.excluded, true);
        this.$emit('filterSelectionChanged', 'Creative Formats');
      },
      onCreativeFormatsSelectionsCleared() {
        this.removeFromCart(this.cart.creativeFormatsInCart);
      },
      onCreativeOrientationsSelectionChanged(val) {
        if (this.allCreativeOrientations == null) {
          return;
        }
        this.removeFromCart(this.cart.creativeOrientationsInCart);
        this.addToCart(val.included);
        this.addToCart(val.excluded, true);
        this.$emit('filterSelectionChanged', 'Creative Orientations');
      },
      onCreativeOrientationsSelectionsCleared() {
        this.removeFromCart(this.cart.creativeOrientationsInCart);
      },
      onIabCategoriesSelectionChanged(val) {
        if (this.allIabCategories == null) {
          return;
        }
        this.removeFromCart(this.cart.iabCategoriesInCart);
        this.addToCart(val.included);
        this.addToCart(val.excluded, true);
        this.$emit('filterSelectionChanged', 'Iab Categories');
      },
      onIabCategoriesSelectionsCleared() {
        this.removeFromCart(this.cart.iabCategoriesInCart);
      },
      async onGeofenceGroupSelectionChanged(val) {
        if (this.geofenceGroups == null) {
          return;
        }
        this.removeFromCart(this.cart.geofencesInCart);
        this.removeFromCart(this.cart.geofenceGroupsInCart);

        const geofenceGroupsWithNoGeofences = val.included.filter((x) => x.geofences?.length === 0);
        if (geofenceGroupsWithNoGeofences.length > 0) {
          const queryParams = QueryParams.all(
            FilterCollection.for(
              'geofencegroup_id',
              geofenceGroupsWithNoGeofences.map((x) => x.id),
              FilterComparisonOperator.IN,
            ),
          );
          queryParams.expand = ['geofences'];
          const geofenceGroupsWithGeofences = await this.getGeofenceGroups({ queryParams });
          geofenceGroupsWithGeofences.items.forEach((gg) =>
            this.updateGeofenceGroupGeofences({
              geofenceGroup: val.included.find((x) => x.id === gg.id),
              value: gg.geofences,
            }),
          );
        }
        this.addToCart(val.included);
        this.addToCart(val.excluded, true);
        this.map.updateGeofenceGroupPolygonsDatasource();
        this.$emit('filterSelectionChanged', 'Geofence Group');
      },
      onGeofenceGroupSelectionsCleared() {
        this.removeFromCart(this.cart.geofenceGroupsInCart);
      },
      onMediaTypeSelectionChanged(val) {
        this.$emit('filterSelectionChanged', 'Venue Type');
        if (this.mediaTypes == null) {
          return;
        }
        this.removeFromCart(this.cart.mediaTypesInCart);
        this.addToCart(val.included);
        this.addToCart(val.excluded, true);
      },
      onMediaTypeSelectionsCleared() {
        this.removeFromCart(this.cart.mediaTypesInCart);
      },
      /**
       * Select the newly created GeofenceGroup in the geofence dropdown and refresh the map and EDS
       * @param {GeofenceGroup} geofenceGroup
       */
      async onProximityGeofenceCreated(geofenceGroup) {
        if (geofenceGroup == null) {
          return; // Don't try to select it if for some reason it is null
        }

        GlobalEventBus.$emit(GlobalEvents.loading);
        await waitUntil(async () => {
          const geofence = await this.getGeofenceGroup({
            id: geofenceGroup.id,
            showLoader: false,
            loadGeofences: false,
          });
          return geofence.isReady;
        }, 2000);
        GlobalEventBus.$emit(GlobalEvents.loaded);

        // Refresh existing geofences
        await this.loadGeofenceGroups();
        // Select created geofence
        const proximityGeofenceDropDown = this.$refs.dd_proximity;
        await waitUntil(() => proximityGeofenceDropDown.isReady && proximityGeofenceDropDown.isInitialized);
        proximityGeofenceDropDown.toggleOption(geofenceGroup);
        proximityGeofenceDropDown.closeDropDown();
      },
      async syncMapContainerActualHeight() {
        await waitUntil(() => this.$refs.mapContainer != null);
        this.mapContainerActualHeight = this.$refs.mapContainer?.offsetHeight;
      },
      enableMouseWheel() {
        this.map.enableMouseScrollZoom();
        this.syncMapContainerActualHeight();
      },
      disableMouseWheel() {
        this.map.disableMouseScrollZoom();
        this.syncMapContainerActualHeight();
      },
      onFilterDropDownHidden(dropdownHidden) {
        this.$emit('filterDropDownHidden');
        if (dropdownHidden == 'Screens' && this.cancelLoadUnitsTokenSource) {
          this.cancelLoadUnitsTokenSource.cancel();
        } else if (dropdownHidden == 'Sites' && this.cancelLoadSitesTokenSource) {
          this.cancelLoadSitesTokenSource.cancel();
        }
      },
      onFilterDropDownVisible(dropdownClicked) {
        this.$emit('filterDropDownVisible', dropdownClicked);
        if (dropdownClicked == 'Screens' && !this.unitsLoaded?.length > 0) {
          this.loadUnits();
        } else if (dropdownClicked == 'Sites' && !this.sitesLoaded?.length > 0) {
          this.loadSites();
        }
      },
      async onUnitPacksFilterDropDownVisible() {
        this.$emit('filterDropDownVisible');
        await this.loadUnitPacks();
      },
      updateSelectedFilters(val) {
        this.selectedFilters.forEach((f) => {
          if (!val.included.some((x) => x.id === f.id)) {
            if (this.$refs[f.id] != null) {
              this.$refs[f.id].clear();
            }
          }
        });
        this.selectedFilters = val.included;
      },
      updateUnitIDs(change) {
        if (this.lineItem) {
          this.updateLineItemUnitIDs({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeUnitIDs({ creative: this.creative, value: change });
        }
      },
      updateUnitExclusionIDs(change) {
        if (this.lineItem) {
          this.updateLineItemUnitExclusionIDs({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeUnitExclusionIDs({ creative: this.creative, value: change });
        }
      },
      updateSiteIDs(change) {
        if (this.lineItem) {
          this.updateLineItemSiteIDs({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeSiteIDs({ creative: this.creative, value: change });
        }
      },
      updateSiteExclusionIDs(change) {
        if (this.lineItem) {
          this.updateLineItemSiteExclusionIDs({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeSiteExclusionIDs({ creative: this.creative, value: change });
        }
      },
      updateLocationIDs(change) {
        if (this.lineItem) {
          this.updateLineItemLocationIDs({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeLocationIDs({ creative: this.creative, value: change });
        }
      },
      updateLocationExclusionIDs(change) {
        if (this.lineItem) {
          this.updateLineItemLocationExclusionIDs({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeLocationExclusionIDs({ creative: this.creative, value: change });
        }
      },
      updateMarketIDs(change) {
        if (this.lineItem) {
          this.updateLineItemMarketIDs({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeMarketIDs({ creative: this.creative, value: change });
        }
      },
      updateMarketExclusionIDs(change) {
        if (this.lineItem) {
          this.updateLineItemMarketExclusionIDs({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeMarketExclusionIDs({ creative: this.creative, value: change });
        }
      },
      updateNetworkIDs(change) {
        if (this.lineItem) {
          this.updateLineItemNetworkIDs({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeNetworkIDs({ creative: this.creative, value: change });
        }
      },
      updateNetworkExclusionIDs(change) {
        if (this.lineItem) {
          this.updateLineItemNetworkExclusionIDs({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeNetworkExclusionIDs({ creative: this.creative, value: change });
        }
      },
      updateLanguageIDs(change) {
        if (this.lineItem) {
          this.updateLineItemLanguageIDs({ lineItem: this.lineItem, value: change });
        }

        if (this.package) {
          this.updatePackageLanguageIDs({ package: this.package, value: change });
        }
      },
      updateLanguageExclusionIDs(change) {
        if (this.lineItem) {
          this.updateLineItemLanguageExclusionIDs({ lineItem: this.lineItem, value: change });
        }

        if (this.package) {
          this.updatePackageLanguageExclusionIDs({ package: this.package, value: change });
        }
      },
      updateMediaTypeIDs(change) {
        if (this.lineItem) {
          this.updateLineItemMediaTypeIDs({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeMediaTypeIDs({ creative: this.creative, value: change });
        }
      },
      updateMediaTypeExclusionIDs(change) {
        if (this.lineItem) {
          this.updateLineItemMediaTypeExclusionIDs({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeMediaTypeExclusionIDs({ creative: this.creative, value: change });
        }
      },
      updateGeofenceGroupIDs(change) {
        if (this.lineItem) {
          this.updateLineItemGeofenceGroupIDs({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeGeofenceGroupIDs({ creative: this.creative, value: change });
        }
      },
      updateGeofenceGroupExclusionIDs(change) {
        if (this.lineItem) {
          this.updateLineItemGeofenceGroupExclusionIDs({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeGeofenceGroupExclusionIDs({ creative: this.creative, value: change });
        }
      },
      updateUnitTags(change) {
        if (this.lineItem) {
          this.updateLineItemUnitTags({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeUnitTags({ creative: this.creative, value: change });
        }
      },
      updateUnitTagExclusions(change) {
        if (this.lineItem) {
          this.updateLineItemUnitTagExclusions({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeUnitTagExclusions({ creative: this.creative, value: change });
        }
      },
      updateUnitPacks(change) {
        if (this.lineItem) {
          this.updateLineItemUnitPackIDs({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeUnitPackIDs({ creative: this.creative, value: change });
        }
      },
      updateUnitPackExclusions(change) {
        if (this.lineItem) {
          this.updateLineItemUnitPackExclusionIDs({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeUnitPackExclusionIDs({ creative: this.creative, value: change });
        }
      },
      updateCreativeFormats(change) {
        if (this.lineItem) {
          this.updateLineItemCreativeFormats({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeCreativeFormats({ creative: this.creative, value: change });
        }
      },
      updateCreativeFormatExclusions(change) {
        if (this.lineItem) {
          this.updateLineItemCreativeFormatExclusions({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeCreativeFormatExclusions({ creative: this.creative, value: change });
        }
      },
      updateCreativeOrientations(change) {
        if (this.lineItem) {
          this.updateLineItemCreativeOrientations({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeCreativeOrientations({ creative: this.creative, value: change });
        }
      },
      updateCreativeOrientationExclusions(change) {
        if (this.lineItem) {
          this.updateLineItemCreativeOrientationExclusions({ lineItem: this.lineItem, value: change });
        } else if (this.creative) {
          this.updateCreativeCreativeOrientationExclusions({ creative: this.creative, value: change });
        }
      },
      updateIabCategories(change) {
        if (this.lineItem) {
          this.updateLineItemIabCategories({ lineItem: this.lineItem, value: change });
        }
      },
      updateIabCategoryExclusions(change) {
        if (this.lineItem) {
          this.updateLineItemIabCategoryExclusions({ lineItem: this.lineItem, value: change });
        }
      },
      async initCartFromEntity(ent, updateSelectedFilters = true) {
        // Get location entities from IDs.
        let locations = [];
        const locationIDs = [...(ent.locationIDs || []), ...(ent.locationExclusionIDs || [])];
        if (locationIDs.length > 0) {
          locations = await this.getLocations({
            queryParams: QueryParams.for({
              top: null,
              filterCollection: new FilterCollection([
                new Filter('location_id', locationIDs, FilterComparisonOperator.IN),
              ]),
            }),
          });
        }

        const includedLocations =
          locations?.items != null
            ? locations.items.filter((x) => ent.locationIDs.find((y) => x.id === y) != null)
            : null;
        const excludedLocations =
          locations?.items != null && ent.locationExclusionIDs != null
            ? locations.items.filter((x) => ent.locationExclusionIDs.find((y) => x.id === y) != null)
            : null;

        //
        // CAM-4538: Uncomment when BE will process "AND" instead or "OR"
        // Buyer only : Filtering has to be default to the countries of the org when no targeting exist at creation
        // !!! Skip this in case the user is buying a deal !!!
        //
        // if (this.entity?.isNew && this.isDemandOrganization && includedLocations == null && excludedLocations == null && this.lineItem?.packageID == null) {
        //   if (this.organization.countries?.length !== this.allCountries?.length) {
        //     locations = await this.getLocations({
        //       queryParams: QueryParams.for({
        //         top: null,
        //         filterCollection: new FilterCollection([
        //           new Filter('level', [0]),
        //           new Filter('code', this.organization.countries, FilterComparisonOperator.IN),
        //         ]),
        //       }),
        //     });
        //     includedLocations = locations?.items ?? null;
        //   }
        // }

        await this.initCart(
          ent.unitIDs,
          ent.unitExclusionIDs != null ? ent.unitExclusionIDs : [],
          ent.siteIDs,
          ent.siteExclusionIDs != null ? ent.siteExclusionIDs : [],
          includedLocations,
          excludedLocations,
          ent.marketIDs,
          ent.marketExclusionIDs != null ? ent.marketExclusionIDs : [],
          ent.geofenceGroupIDs,
          ent.geofenceGroupExclusionIDs != null ? ent.geofenceGroupExclusionIDs : [],
          ent.mediaTypeIDs,
          ent.mediaTypeExclusionIDs != null ? ent.mediaTypeExclusionIDs : [],
          ent.networkIDs == null ? [] : ent.networkIDs,
          ent.networkExclusionIDs != null ? ent.networkExclusionIDs : [],
          ent.unitTags,
          ent.unitTagExclusions != null ? ent.unitTagExclusions : [],
          ent.unitPackIDs,
          ent.unitPackExclusionIDs != null ? ent.unitPackExclusionIDs : [],
          ent.creativeFormats != null ? ent.creativeFormats : [],
          ent.creativeFormatExclusions != null ? ent.creativeFormatExclusions : [],
          ent.languageIDs != null ? ent.languageIDs : [],
          ent.languageExclusionIDs != null ? ent.languageExclusionIDs : [],
          ent.creativeOrientations != null ? ent.creativeOrientations : [],
          ent.creativeOrientationExclusions != null ? ent.creativeOrientationExclusions : [],
          ent.iabCategories != null ? ent.iabCategories : [],
          ent.iabCategoryExclusions != null ? ent.iabCategoryExclusions : [],
        );
        const hasSelectedCities = locations?.items != null && locations.items.filter((l) => l.level === 3).length > 0;
        const hasSelectedRegions = locations?.items != null && locations.items.filter((l) => l.level === 1).length > 0;
        const hasSelectedCountries =
          locations?.items != null && locations.items.filter((l) => l.level === 0).length > 0;
        const selectedFilters = this.availableFilters.filter(
          (x) =>
            (x.id === 'dd_markets' && (ent.marketIDs?.length > 0 || ent.marketExclusionIDs?.length > 0)) ||
            (x.id === 'dd_media_owners' && ent.mediaOwnerIDs?.length > 0) ||
            (x.id === 'dd_media_types' && (ent.mediaTypeIDs?.length > 0 || ent.mediaTypeExclusionIDs?.length > 0)) ||
            (x.id === 'dd_networks' && (ent.networkIDs?.length > 0 || ent.networkExclusionIDs?.length > 0)) ||
            (x.id === 'dd_proximity' &&
              (ent.geofenceGroupIDs?.length > 0 || ent.geofenceGroupExclusionIDs?.length > 0)) ||
            (x.id === 'dd_sites' && (ent.siteIDs?.length > 0 || ent.siteExclusionIDs?.length > 0)) ||
            (x.id === 'dd_units' && (ent.unitIDs?.length > 0 || ent.unitExclusionIDs?.length > 0)) ||
            (x.id === 'dd_cities' && hasSelectedCities) ||
            (x.id === 'dd_regions' && hasSelectedRegions) ||
            (x.id === 'dd_countries' && hasSelectedCountries) ||
            (x.id === 'dd_unitpacks' && (ent.unitPackIDs?.length > 0 || ent.unitPackExclusionIDs?.length > 0)) ||
            (x.id === 'dd_tags' && (ent.unitTags?.length > 0 || ent.unitTagExclusions?.length > 0)) ||
            (x.id === 'dd_creative_formats' &&
              (ent.creativeFormats?.length > 0 || ent.creativeFormatExclusions?.length > 0)) ||
            (x.id === OverallMapDropdownFilter.Languages &&
              (ent.languageIDs?.length > 0 || ent.languageExclusionIDs?.length > 0)) ||
            (x.id === OverallMapDropdownFilter.CreativeOrientations &&
              (ent.creativeOrientations?.length > 0 || ent.creativeOrientationExclusions?.length > 0)) ||
            (x.id === OverallMapDropdownFilter.IabCategories &&
              (ent.iabCategories?.length > 0 || ent.iabCategoryExclusions?.length > 0)),
        );

        if (this.lineItem != null && this.lineItem.id === -1 && !this.isReadOnly) {
          let defaultFilters = [];
          if (
            this.membershipSettings?.uiDefaultLineItemMapFilters != null &&
            this.membershipSettings.uiDefaultLineItemMapFilters.trim() !== ''
          ) {
            const names = this.membershipSettings.uiDefaultLineItemMapFilters.split(',');
            names.forEach((name) => {
              const filter = this.availableFilters.find((x) => x.id === `dd_${name}`);
              if (filter != null) {
                defaultFilters.push(filter);
              }
            });
          }
          defaultFilters =
            defaultFilters.length > 0 ? defaultFilters : this.availableFilters.filter((x) => x.isDefault);
          // When buying a deal, some geography could be set, make sure they appear in the filters.
          defaultFilters = [
            ...selectedFilters.filter((f) => !defaultFilters.some((df) => df.id === f.id)),
            ...defaultFilters,
          ];

          // When buying a deal, some filters might not be available if they are predefined without selected values.
          if (this.areMarketsReadOnly && ent.marketIDs.length === 0) {
            defaultFilters = defaultFilters.filter((f) => f.id !== 'dd_markets');
          }
          if (this.areMediaTypesReadOnly && ent.mediaTypeIDs.length === 0) {
            defaultFilters = defaultFilters.filter((f) => f.id !== 'dd_media_types');
          }
          if (this.areNetworksReadOnly && ent.networkIDs.length === 0) {
            defaultFilters = defaultFilters.filter((f) => f.id !== 'dd_networks');
          }
          if (this.areLocationsReadOnly && ent.geofenceGroupIDs.length === 0) {
            defaultFilters = defaultFilters.filter((f) => f.id !== 'dd_proximity');
          }
          if (this.areSitesReadOnly && ent.siteIDs.length === 0) {
            defaultFilters = defaultFilters.filter((f) => f.id !== 'dd_sites');
          }
          if (this.areUnitsReadOnly && ent.unitIDs.length === 0) {
            defaultFilters = defaultFilters.filter((f) => f.id !== 'dd_units');
          }
          if (this.areLocationsReadOnly && !hasSelectedCities) {
            defaultFilters = defaultFilters.filter((f) => f.id !== 'dd_cities');
          }
          if (this.areLocationsReadOnly && !hasSelectedRegions) {
            defaultFilters = defaultFilters.filter((f) => f.id !== 'dd_regions');
          }
          if (this.areLocationsReadOnly && !hasSelectedCountries) {
            defaultFilters = defaultFilters.filter((f) => f.id !== 'dd_countries');
          }
          if (this.areTagsReadOnly && ent.unitTags.length === 0 && ent.unitTagExclusions.length === 0) {
            defaultFilters = defaultFilters.filter((f) => f.id !== 'dd_tags');
          }
          if (
            this.areCreativeFormatsReadOnly &&
            ent.creativeFormats.length === 0 &&
            ent.creativeFormatExclusions.length === 0
          ) {
            defaultFilters = defaultFilters.filter((f) => f.id !== 'dd_creative_formats');
          }
          if (
            this.areCreativeOrientationsReadOnly &&
            ent.creativeOrientations.length === 0 &&
            ent.creativeOrientationExclusions.length === 0
          ) {
            defaultFilters = defaultFilters.filter((f) => f.id !== OverallMapDropdownFilter.CreativeOrientations);
          }
          if (
            this.areIabCategoriesReadOnly &&
            ent.iabCategories.length === 0 &&
            ent.iabCategoryExclusions.length === 0
          ) {
            defaultFilters = defaultFilters.filter((f) => f.id !== OverallMapDropdownFilter.IabCategories);
          }
          if (updateSelectedFilters) {
            this.selectedFilters = defaultFilters;
          }
        } else if (updateSelectedFilters) {
          if (this.network != null) {
            this.selectedFilters = this.availableFilters.filter((f) => f.id === 'dd_units');
          } else {
            this.selectedFilters = selectedFilters;
          }
        }
      },
      async loadLanguages() {
        await this.getLanguages({ showLoader: false });
      },
      async onSitesDropDownSearchChanged(searchTerm) {
        this.sitesSearchTerm = searchTerm;
        this.sitesFromSearchLoaded = [];
        this.sitesFromSearchTotalCount = 0;

        if (searchTerm?.length > 0 || !this.sitesLoaded?.length > 0) {
          await this.loadSites(searchTerm, 0);
        } else {
          if (this.cancelLoadSitesTokenSource != null) {
            this.cancelLoadSitesTokenSource.cancel();
          }
          const ddSites = this.availableFilters.find((x) => x.id === 'dd_sites');
          if (ddSites) {
            ddSites.isLoading = false;
          }
          this.isLoadingMoreSites = false;
          this.showNoSitesFoundMsg = this.sitesForFilters?.length === 0;
        }
      },
      async onSitesDropDownLoadMoreOptions() {
        if (this.sitesSearchTerm?.length > 0) {
          await this.loadSites(this.sitesSearchTerm, this.sitesFromSearchLoaded.length);
        } else {
          await this.loadSites(this.sitesSearchTerm, this.sitesLoaded.length);
        }
      },
      async onUnitsDropDownSearchChanged(searchTerm) {
        this.unitsFromSearchLoaded = [];
        this.unitsFromSearchTotalCount = 0;
        this.unitsSearchTerm = searchTerm;

        if (searchTerm?.length > 0 || !this.unitsLoaded?.length > 0) {
          await this.loadUnits(searchTerm, 0);
        } else {
          if (this.cancelLoadUnitsTokenSource != null) {
            this.cancelLoadUnitsTokenSource.cancel();
          }
          const ddUnits = this.availableFilters.find((x) => x.id === 'dd_units');
          if (ddUnits) {
            ddUnits.isLoading = false;
          }
          this.isLoadingMoreUnits = false;
          this.showNoUnitsFoundMsg = this.unitsForFilters?.length === 0;
        }
      },
      async onUnitsDropDownLoadMoreOptions() {
        if (this.unitsSearchTerm?.length > 0) {
          await this.loadUnits(this.unitsSearchTerm, this.unitsFromSearchLoaded.length);
        } else {
          await this.loadUnits(this.unitsSearchTerm, this.unitsLoaded.length);
        }
      },
      async onUnitPacksDropDownSearchChanged(searchTerm) {
        await this.loadUnitPacks(searchTerm);
      },
      setupAvailableFilters() {
        const ddMediaTypes = {
          id: 'dd_media_types',
          name: capitalize(i18n.global.t('names.venueType', { n: 2 })),
          isDefault: true,
        };
        const ddMarkets = {
          id: 'dd_markets',
          name: capitalize(i18n.global.t('names.market', { n: 2 })),
          isDefault: true,
        };
        const ddNetworks = {
          id: 'dd_networks',
          name: capitalize(i18n.global.t('names.network', { n: 2 })),
          isDefault: true,
        };
        const ddSites = {
          id: 'dd_sites',
          name: capitalize(i18n.global.t('names.site', { n: 2 })),
          isLoading: false,
        };
        const ddUnits = {
          id: 'dd_units',
          name: capitalize(i18n.global.t('names.unit', { n: 2 })),
          isLoading: false,
        };
        const ddRegions = {
          id: 'dd_regions',
          name: capitalize(i18n.global.t('names.region', { n: 2 })),
          isDefault: this.isDisplayVideoLineItem,
        };
        const ddCountries = {
          id: 'dd_countries',
          name: capitalize(i18n.global.t('names.country', { n: 2 })),
          isDefault: this.isDisplayVideoLineItem,
        };
        const ddCities = {
          id: 'dd_cities',
          name: capitalize(i18n.global.t('names.city', { n: 2 })),
          isDefault: this.isDisplayVideoLineItem,
        };
        const ddProximity = {
          id: 'dd_proximity',
          name: capitalize(i18n.global.t('names.proximity', { n: 1 })),
          isDefault: true,
        };
        const ddTags = {
          id: 'dd_tags',
          name: capitalize(i18n.global.t('names.tag', { n: 2 })),
        };
        const ddCreativeFormats = {
          id: 'dd_creative_formats',
          name: capitalize(i18n.global.t('names.creativeFormat', { n: 2 })),
        };
        const ddUnitPacks = {
          id: 'dd_unitpacks',
          name: capitalize(i18n.global.t('names.unitpacks', { n: 2 })),
          isLoading: false,
        };
        const ddLanguages = {
          id: OverallMapDropdownFilter.Languages,
          name: capitalize(i18n.global.t('names.language', { n: 2 })),
        };
        const ddCreativeOrientations = {
          id: OverallMapDropdownFilter.CreativeOrientations,
          name: capitalize(i18n.global.t('names.creativeOrientation', { n: 2 })),
        };
        const ddIabCategories = {
          id: OverallMapDropdownFilter.IabCategories,
          name: capitalize(i18n.global.t('names.iabCategory', { n: 2 })),
        };
        let filters = [
          ...(this.filteredMediaTypes.length > 0 ? [ddMediaTypes] : []),
          ddMarkets,
          ...(this.showNetworksFilter ? [ddNetworks] : []),
          ...(this.showUnitsDropDownFilter ? [ddUnits] : []),
          ...(this.membershipSettings?.uiEnableLineItemSiteTargeting != null ? [ddSites] : []),
          ...[ddProximity],
          ...(this.canShowUnitPacks ? [ddUnitPacks] : []),
          ...[ddCountries, ddRegions, ddCities, ddTags, ddCreativeFormats, ddCreativeOrientations],
        ];

        // Lang filter is available for everything except creatives
        if (!this.creative) {
          filters = [...filters, ddLanguages, ddIabCategories];
        }

        // Sidekick: Sites and Tags are excluded
        if (this.isSidekickOrganization) {
          filters = filters.filter((filter) => filter != ddSites && filter != ddTags);
        }

        if (
          this.membershipSettings?.uiAvailableLineItemMapFilters != null &&
          this.membershipSettings.uiAvailableLineItemMapFilters.trim() !== ''
        ) {
          const availableFilterNames = this.membershipSettings.uiAvailableLineItemMapFilters.trim().split(',');
          filters = filters.filter((x) => availableFilterNames.some((f) => `dd_${f}` === x.id));
        }

        if (this.isDisplayVideoLineItem) {
          const availableFiltersForDvLi = ['dd_regions', 'dd_countries', 'dd_cities'];
          filters = filters.filter((x) => availableFiltersForDvLi.includes(x.id));
        }
        // For networks only allow screen filter
        this.availableFilters = this.network != null ? filters.filter((f) => f.id === 'dd_units') : filters;
      },
      clearFilters() {
        this.filters = [];
        this.cart.clear();
        this.index += 1;
      },
      initFilters() {
        this.setupAvailableFilters();

        //
        // Add event handler for whenever the cart selections change.
        //
        this.cart.changedCallback = (cart) => {
          // Don't update the cart if the filters restoration is in progress.
          if (!this.isFiltersRestored) return;

          if (this.entity != null) {
            let changed = false;
            if (!areArraysEqual(this.entity.unitIDs, cart.includedUnitIDs)) {
              this.updateUnitIDs(cart.includedUnitIDs);
              changed = true;
            }
            if (!areArraysEqual(this.entity.unitExclusionIDs, cart.excludedUnitIDs)) {
              this.updateUnitExclusionIDs(cart.excludedUnitIDs);
              changed = true;
            }
            if (!areArraysEqual(this.entity.siteIDs, cart.includedSiteIDs)) {
              this.updateSiteIDs(cart.includedSiteIDs);
              changed = true;
            }
            if (!areArraysEqual(this.entity.siteExclusionIDs, cart.excludedSiteIDs)) {
              this.updateSiteExclusionIDs(cart.excludedSiteIDs);
              changed = true;
            }
            if (!areArraysEqual(this.entity.locationIDs, cart.includedLocationIDs)) {
              this.updateLocationIDs(cart.includedLocationIDs);
              changed = true;
            }
            if (!areArraysEqual(this.entity.locationExclusionIDs, cart.excludedLocationIDs)) {
              this.updateLocationExclusionIDs(cart.excludedLocationIDs);
              changed = true;
            }
            if (!areArraysEqual(this.entity.marketIDs, cart.includedMarketIDs)) {
              this.updateMarketIDs(cart.includedMarketIDs);
              changed = true;
            }
            if (!areArraysEqual(this.entity.marketExclusionIDs, cart.excludedMarketIDs)) {
              this.updateMarketExclusionIDs(cart.excludedMarketIDs);
              changed = true;
            }
            if (!areArraysEqual(this.entity.networkIDs, cart.includedNetworkIDs)) {
              this.updateNetworkIDs(cart.includedNetworkIDs);
              changed = true;
            }
            if (!areArraysEqual(this.entity.networkExclusionIDs, cart.excludedNetworkIDs)) {
              this.updateNetworkExclusionIDs(cart.excludedNetworkIDs);
              changed = true;
            }
            if (!areArraysEqual(this.entity.mediaTypeIDs, cart.includedMediaTypeIDs)) {
              this.updateMediaTypeIDs(cart.includedMediaTypeIDs);
              changed = true;
            }
            if (!areArraysEqual(this.entity.mediaTypeExclusionIDs, cart.excludedMediaTypeIDs)) {
              this.updateMediaTypeExclusionIDs(cart.excludedMediaTypeIDs);
              changed = true;
            }
            if (!areArraysEqual(this.entity.geofenceGroupIDs, cart.includedGeofenceGroupIDs)) {
              this.updateGeofenceGroupIDs(cart.includedGeofenceGroupIDs);
              changed = true;
            }
            if (!areArraysEqual(this.entity.geofenceGroupExclusionIDs, cart.excludedGeofenceGroupIDs)) {
              this.updateGeofenceGroupExclusionIDs(cart.excludedGeofenceGroupIDs);
              changed = true;
            }
            if (!areArraysEqual(this.entity.languageIDs, cart.includedLanguageIds)) {
              this.updateLanguageIDs(cart.includedLanguageIds);
              changed = true;
            }
            if (!areArraysEqual(this.entity.languageExclusionIDs, cart.excludedLanguageIds)) {
              this.updateLanguageExclusionIDs(cart.excludedLanguageIds);
              changed = true;
            }
            if (
              !areArraysEqual(
                this.entity.unitTags,
                cart.includedTags.map((x) => x.tag),
              )
            ) {
              this.updateUnitTags(cart.includedTags.map((x) => x.tag));
              changed = true;
            }
            if (
              !areArraysEqual(
                this.entity.unitTagExclusions,
                cart.excludedTags.map((x) => x.tag),
              ) ||
              !areArraysEqual(this.entity.unitTagExclusions, this.defaultExcludedTags?.map((x) => x.tag) ?? [])
            ) {
              this.updateUnitTagExclusions(cart.excludedTags.map((x) => x.tag));
              changed = true;
            }
            if (!areArraysEqual(this.entity.unitPackIDs, cart.includedUnitPackIDs)) {
              this.updateUnitPacks(cart.includedUnitPackIDs);
              changed = true;
            }
            if (!areArraysEqual(this.entity.unitPackExclusionIDs, cart.excludedUnitPackIDs)) {
              this.updateUnitPackExclusions(cart.excludedUnitPackIDs);
              changed = true;
            }
            if (
              !areArraysEqual(
                this.entity.creativeFormats,
                cart.includedCreativeFormats.map((x) => x.id),
              )
            ) {
              this.updateCreativeFormats(cart.includedCreativeFormats.map((x) => x.id));
              changed = true;
            }
            if (
              !areArraysEqual(
                this.entity.creativeFormatExclusions,
                cart.excludedCreativeFormats.map((x) => x.id),
              )
            ) {
              this.updateCreativeFormatExclusions(cart.excludedCreativeFormats.map((x) => x.id));
              changed = true;
            }
            if (
              !areArraysEqual(
                this.entity.creativeOrientations,
                cart.includedCreativeOrientations.map((x) => x.id),
              )
            ) {
              this.updateCreativeOrientations(cart.includedCreativeOrientations.map((x) => x.id));
              changed = true;
            }
            if (
              !areArraysEqual(
                this.entity.creativeOrientationExclusions,
                cart.excludedCreativeOrientations.map((x) => x.id),
              )
            ) {
              this.updateCreativeOrientationExclusions(cart.excludedCreativeOrientations.map((x) => x.id));
              changed = true;
            }
            if (!areArraysEqual(this.entity.iabCategories, cart.includedIabCategoryIDs)) {
              this.updateIabCategories(cart.includedIabCategories.map((x) => x.id));
              changed = true;
            }
            if (!areArraysEqual(this.entity.iabCategoryExclusions, cart.excludedIabCategoryIDs)) {
              this.updateIabCategoryExclusions(cart.excludedIabCategories.map((x) => x.id));
              changed = true;
            }
            if (changed || this.isFirstClusterLoad) {
              this.updateMapAccordingToFilters(true);
              // I need the Creative itself to get mediaowner approval IDs
              this.$emit('cartChanged', this.isFromCreativePendingApprovals ? this.entity : cart);
            }
          } else {
            this.updateMapAccordingToFilters(true);
            // I need the Creative itself to get mediaowner approval IDs
            this.$emit('cartChanged', this.isFromCreativePendingApprovals ? this.entity : cart);
          }
        };
      },
      openProximitySearchModal(id = null) {
        this.$refs.proximityGeofenceModal.open(id);
        if (id == null) {
          GoogleAnalyticsApi.sendEvent('CreateNewProximityGeofenceInInventoryMap', {
            category: 'ProximityGeofence',
          });
        }
      },
      async initMap() {
        this.isReferenceDataReady = true;
        this.hasStartedFiltering = this.isReadOnly;
        // no need to init map from entity when using a public shared plan
        if (this.entity != null && !this.mapDataProvider.isPublicPlan) {
          this.initCartFromEntity(this.entity);
        } else {
          this.selectedFilters = this.availableFilters.filter((x) => x.isDefault);
          this.updateMapAccordingToFilters(true);
        }

        if (this.mapDataProvider.isPublicPlan) {
          const promises = [];
          // Show targeted geofence groups on shared plan page
          if (this.entity.geofenceGroupIDs.length > 0) {
            promises.push(
              this.loadGeofenceSelections(this.entity.geofenceGroupIDs, this.entity.geofenceGroupExclusionIDs),
            );
          }
          if (this.isStroeerOrganization) {
            promises.push(this.loadActiveNetworks());
          }

          await Promise.all(promises);
        }

        window.addEventListener('resize', this.syncMapContainerActualHeight);
        this.syncMapContainerActualHeight();
      },
      getMediaOwnerName(mediaOwnerID) {
        return this.mediaOwners
          .filter((mo) => mediaOwnerID === mo.id)
          .map((x) => x.name)
          .join(', ');
      },
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use 'sass:color';
  @use '@/styles/variables.scss' as *;

  #app {
    .overview-map-container {
      display: flex;
      flex-direction: column;
      position: relative;
      flex-grow: 1;

      /*.loader {*/
      /*  position: absolute;*/
      /*  top: 0;*/
      /*  bottom: 0;*/
      /*  left: 0;*/
      /*  right: 0;*/
      /*  background: #00000020;*/
      /*  z-index: 1;*/
      /*}*/

      &.has-targeting-summary {
        height: 73vh !important;
      }

      .stats {
        padding: 0.3rem 1.6rem 0.3rem 0.8rem;
        border-bottom: solid 1px #eee;
        background: #fff;
        display: flex;
        align-items: center;
        flex-shrink: 0;
        box-shadow: $small-shadow;
        z-index: 1;
      }

      .active-filters {
        padding: 1.6rem 3.2rem;
        box-shadow: $small-shadow;
        display: flex;
        border-bottom: solid 0.1rem $borders;

        background: var(--background);

        .filter-group {
          .name {
            font-size: 0.8em;
            text-transform: uppercase;
            margin-bottom: 0.3rem;
            margin-right: 1.6rem;
            border-left: solid 0.1rem $borders-light;
            padding-left: 1.6rem;

            color: var(--secondaryColor);
          }

          .values {
            display: flex;
            align-items: center;

            .value {
              font-weight: 600;
              margin-right: 0.6rem;

              &:after {
                content: ', ';
              }

              &:last-of-type {
                margin-right: 0;

                &:after {
                  content: '';
                }
              }
            }
          }
        }
      }

      .filters-and-map {
        display: flex;

        .filters-pane {
          transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
          margin-left: -35rem;
          width: 35rem;
          max-width: 35rem;
          padding: 0.8rem;
          overflow-y: auto;
          /*border-right: solid 0.1rem rgba(0, 0, 0, 0.1);*/
          z-index: 1;
          left: 0;
          bottom: 0;
          position: absolute;
          top: 0;
          pointer-events: none;

          &.visible {
            transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
            margin-left: 0;
          }

          .filters-toggle-btn {
            position: fixed;
            margin-left: 34.2rem;
            border-radius: 0 0.6rem 0.6rem 0;
          }
        }

        .create-proximity-geofence-button-container {
          display: flex;
          align-items: center;
          flex-direction: column;
          padding: 1rem;
        }
      }

      .map-tools {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;

        .map-btn {
          box-shadow: 0 0 1rem rgba(77, 77, 77, 0.48);
          background-color: #fff;
          pointer-events: auto;
          margin-top: 0.8rem;
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          border-radius: 0.4rem;
          align-items: center;
          flex-basis: 30%;
          /*flex-basis: 48%;*/
          padding: 1.6rem;
          outline: none;
          cursor: pointer;
          transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);

          svg {
            height: 2.4rem;
            width: 2.4rem;
            stroke: var(--textColor);
            fill: var(--textColor);
          }

          &:hover {
            transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
            background: var(--primaryColor);
            color: var(--primaryColorReverse);

            svg {
              stroke: var(--primaryColorReverse);
            }
          }
        }
      }

      .map-pane {
        background: #fff;
        box-shadow: var(--panesShadow);
        background: var(--panesBackgroundColor);
        pointer-events: auto;
        margin-top: 0.8rem;
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        border-radius: 0.4rem;
        z-index: 1;

        &.padded {
          padding: 0rem 1rem 0.6rem 1rem;
        }

        &.top-margin {
          margin-top: 4rem;
        }

        &.fading {
          opacity: 0.9;
          transition: opacity 0.2s ease-in-out;

          &:hover {
            opacity: 1;
            transition: opacity 0.2s ease-in-out;
          }
        }

        &.fill {
          flex-shrink: 0;
        }

        &.shrink {
          flex-grow: 0;
          flex-shrink: 1;
        }

        &:first-of-type {
          margin-top: 0;
          flex-shrink: 0;
        }

        &.map-pane-search {
          height: 4.8rem;
          flex-basis: 4.8rem;
          flex-shrink: 0;
        }

        &.location-details-pane {
          .street-view-image {
            width: 100%;
          }

          .supported-content {
            display: flex;
            justify-content: space-evenly;

            div {
              display: flex;
              flex-direction: column;
              align-items: center;

              svg {
                width: 3.2rem;
                height: 3.2rem;
              }

              span {
                font-size: 0.9em;
                font-weight: 600;
                margin-top: 0.3rem;
              }
            }
          }

          .screen-dimensions-subsection {
            background: var(--background);

            box-shadow: inset 0 0 2.4rem rgba(0, 0, 0, 0.15);
            border-color: color.adjust($borders-light, $lightness: -10%) !important;
            padding-top: 1.6rem !important;
          }

          .screen-dimensions-container {
            display: flex;
            align-content: center;
            justify-content: center;

            .screen-dimensions-frame {
              border: solid 0.1rem rgba(0, 0, 0, 0.15);
              background: var(--secondaryColorReverseFaded50);

              padding: 0.8rem;
              box-shadow: $small-shadow;

              .screen-dimensions {
                border: solid 0.1rem var(--secondaryColorFaded50);
                background: var(--secondaryColorReverse);

                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;

                span {
                  color: var(--secondaryColor);

                  font-size: 1.1rem;
                  text-align: center;

                  &:nth-of-type(2n + 1) {
                    font-size: 1.6rem;
                  }

                  &:nth-of-type(2n) {
                    margin: 0 1rem;
                  }
                }
              }
            }
          }
        }

        .map-pane-footer {
          border-top: solid 0.1rem rgba(0, 0, 0, 0.1);
          display: flex;
          flex-shrink: 0;

          button {
            flex-basis: 100%;
            justify-content: center;
            border-radius: 0;
          }
        }

        .map-pane-header {
          font-weight: 700;
          border-bottom: solid 0.1rem $borders;
          display: flex;
          flex-direction: row;
          flex-shrink: 0;
          /*box-shadow: 0 0.1rem 0 rgba(0, 0, 0, 0.05);*/
          align-items: center;
          padding: 0.3rem 0;

          svg {
            width: 1.6rem;
            height: 1.6rem;
            margin-left: 0.8rem;
          }

          > div {
            display: flex;
            padding: 0.8rem 0.8rem 0.8rem 0.8rem;
          }

          span {
            padding: 0.8rem 0.8rem 0.8rem 0.8rem;
            flex-grow: 1;

            svg {
              fill: var(--primaryColor);
            }
          }

          .actions {
            display: flex;

            .btn {
              margin-left: 0.3rem;
              padding: 0.8rem 1.4rem;

              &:first-of-type {
                margin-left: 0;
              }

              svg {
                width: 1rem;
                height: 1rem;
              }
            }
          }
        }

        .map-pane-content {
          font-size: 0.9em;
          display: flex;
          flex-direction: column;
          -webkit-overflow-scrolling: touch;
          transform-origin: top;
          animation-name: scaleVertical;
          animation-duration: 0.5s;
          animation-timing-function: cubic-bezier(0.2, 1, 0.2, 1);
          animation-fill-mode: backwards;

          &.no-scroll {
            overflow: visible;
          }

          &.padded {
            padding: 0.6rem;
          }

          a {
            cursor: pointer;

            svg {
              height: 1.6rem;
              width: 1.6rem;

              stroke: var(--secondaryColor);
              fill: var(--secondaryColor);
            }
          }

          hr {
            padding: 0;
            margin: 0;
            border-style: dotted;
          }

          .location-image-container {
            position: relative;
            display: flex;
            flex-direction: column;
            flex-shrink: 0;
          }

          .header {
            background-color: var(--tableHeaderBackgroundColor);
            color: var(--tableHeaderForegroundColor);

            padding: 0.8rem 1rem;
            font-weight: 700;
            display: flex;
            flex-direction: row;
            align-items: center;
            flex: 1 0 auto;
            font-size: 1.2rem;
            border-top: solid 0.1rem $borders;

            svg {
              fill: var(--textColor);
              stroke: var(--textColor);

              min-width: 1.6rem;
              width: 1.6rem;
              height: 1.6rem;
              margin-right: 1rem;
            }

            span {
              flex-grow: 1;
            }
          }

          .units-header,
          .sites-header {
            background-color: var(--secondaryColor);
            color: var(--secondaryColorReverse);

            padding: 1.6rem 2rem;
            font-weight: 700;
            font-size: 1.4rem;
            display: flex;
            flex-direction: row;
            align-items: center;
            flex: 1 0 auto;

            svg {
              fill: var(--secondaryColorReverse);
              stroke: var(--secondaryColorReverse);

              min-width: 2.4rem;
              width: 2.4rem;
              height: 2.4rem;
              margin-right: 1.6rem;
            }

            span {
              flex-grow: 1;
            }
          }

          .unit-header,
          .site-header {
            background-color: var(--secondaryColor);
            color: var(--secondaryColorReverse);

            padding: 1.6rem 2rem;
            font-weight: 700;
            font-size: 1.4rem;
            display: flex;
            flex-direction: row;
            flex: 1 0 auto;
            position: relative;
            overflow: visible;
            box-shadow: 0 1px 6px rgba(0, 0, 0, 0.25);
            z-index: 1;
            line-height: 1.8rem;

            .name-and-id {
              display: flex;
              flex-grow: 1;
              flex-direction: column;

              small {
                margin-top: 0.3rem;
                font-weight: 400;
              }
            }

            .paging {
              align-items: flex-end;
              font-size: 0.8em;

              .btn {
                margin-bottom: 0.2rem;
              }

              span {
                text-align: right;
              }
            }

            > svg {
              fill: var(--secondaryColorReverse);
              stroke: var(--secondaryColorReverse);

              min-width: 2.4rem;
              width: 2.4rem;
              height: 2.4rem;
              margin-right: 1.6rem;
            }

            .back-btn {
              background: var(--actionsColorReverse);
              color: var(--actionsColor);

              cursor: pointer;
              border-radius: 2rem;
              border-width: 0;
              display: flex;
              width: 2.8rem;
              height: 2.8rem;
              outline: none;
              box-shadow: $shadow;
              min-width: auto;
              margin-right: 0.8rem;
              margin-top: -0.4rem;
              position: relative;

              &:hover {
                background: var(--actionsColor);

                svg {
                  fill: var(--actionsColorReverse);
                  stroke: var(--actionsColorReverse);
                }
              }

              svg {
                fill: var(--actionsColor);
                stroke: var(--actionsColor);
                display: block;
                margin: auto;
                height: 1.6rem;
                width: 1.6rem;
                transform: rotate(90deg);
              }
            }

            .circle-btn {
              z-index: 1;
              width: 3.7rem;
              height: 3.7rem;
              margin-left: 22.8rem;
              animation-delay: 0.5s;
              position: absolute;
              right: -18px;
              margin-top: 0;
            }
          }

          .section {
            /*color: #fff;*/
            background-color: rgba(0, 0, 0, 0.1);
            padding: 1.2rem 2rem;
            font-weight: 700;
            font-size: 1.3rem;
            display: flex;
            flex-direction: row;
            flex: 1 0 auto;
            box-shadow: 0 1px 6px rgba(0, 0, 0, 0.25);
            z-index: 1;
          }

          .cart-list,
          .unit-list,
          .site-list {
            margin-bottom: 0;
            padding-left: 0;
            overflow-y: auto;

            .load-more-results {
              padding: 1.2rem 2.4rem;

              svg {
                margin-right: 0.8rem;
              }
            }
          }

          .cart-list {
            .city-selection,
            .country-selection,
            .region-selection,
            .market-selection,
            .network-selection,
            .geofencegroup-selection,
            .mediaowner-selection,
            .mediatype-selection {
              padding: 1.2rem 0 1.2rem 1.2rem;
              height: 5.3rem;

              .icon {
                margin-left: 1rem;
                background: none;
                padding: 0;

                img {
                  max-height: 2.4rem;
                }
              }

              .info-and-actions {
                padding: 0 0.8rem;
              }

              .info {
                max-width: 22.5rem;
              }

              &:hover {
                .info {
                  max-width: 19.5rem;
                }
              }

              .on,
              .off {
                display: none;
              }

              &.show-filter-button {
                .on,
                .off {
                  display: block;
                }

                .info {
                  max-width: 20.5rem;
                }

                &:hover {
                  .info {
                    max-width: 17.5rem;
                  }
                }
              }
            }

            .mediaowner-selection,
            .mediatype-selection {
              .icon {
                img {
                  max-height: 1.6rem;
                }
              }
            }
          }

          ul {
            li {
              .visible-on-hover {
                display: none;
                margin-right: 0.6rem;
              }

              &:hover {
                .visible-on-hover {
                  display: block;
                }

                .hidden-on-hover {
                  display: none;
                }
              }
            }
          }

          .list-actions {
            padding: 0.6rem 1rem;
            background: #fff;
            font-size: 0.9em;
            font-weight: 600;
            text-align: right;
          }

          .demographic-selector-container {
            padding: 0 1.6rem;
          }
        }

        .btn-group {
          justify-content: flex-start;

          .btn-default {
            &:not(:hover) {
              background: #fff;
            }
          }
        }
      }

      .site-details-pane,
      .unit-details-pane {
        .scrollable-content {
          &.padded {
            padding: 1.6rem;
          }

          overflow-y: auto;
        }
      }

      .filters-pane .map-pane.filters-map-pane {
        ul {
          overflow-y: auto;
          max-height: 20rem;

          li {
            display: flex;
            align-items: center;
            padding: 0.6rem 0.8rem;

            > label {
              margin: 0.3rem 0;
              display: flex;
              align-items: center;
              width: 100%;

              > div {
                flex-grow: 1;
              }
            }

            input {
              margin-right: 0.6rem;
            }

            img {
              margin-right: 0.6rem;
              height: 1.6rem;
            }

            .vue-js-switch {
              margin-bottom: 0;
            }

            a {
              /*margin-right: 1.6rem;*/
              opacity: 0;
              animation-name: opacifyAnimation;
              animation-duration: 0.5s;
              animation-timing-function: cubic-bezier(0.2, 1, 0.2, 1);
              animation-fill-mode: forwards;
              pointer-events: none;
              font-weight: 600;
              font-size: 0.9em;
            }

            &:hover {
              a {
                opacity: 1;
                pointer-events: auto;
              }
            }
          }
        }
      }

      .cart {
        position: relative;

        svg {
          height: 3.2rem;
          width: 3.2rem;
        }

        .badge {
          position: absolute;
          right: -0.4rem;
          bottom: -0.4rem;
          border-radius: 1.6rem;
        }
      }

      .audience-info-kpis {
        .separator {
          width: 0.1rem;
          height: 5.2rem;
          border-left: dotted 0.1rem $borders;
          min-width: 0.1rem;
          max-width: 0.1rem;
          margin: 0 1.2rem;
        }

        .kpi.small {
          flex-basis: 0;
          flex-grow: 0;

          .right {
            .label {
              white-space: nowrap;
            }
          }

          .icon {
            svg {
              width: 3.2rem;
              height: 3.2rem;
            }
          }
        }
      }

      .map {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
      }

      .map-panes {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 1rem;
        pointer-events: none;
        display: flex;
        overflow: hidden;

        .map-panes-left {
          width: 33.3rem;
          display: flex;
          flex-direction: column;
          margin-bottom: 1.6rem;
        }

        .map-panes-center {
          flex-grow: 1;
          pointer-events: none;
          margin-left: 1rem;
          margin-right: 3.4rem;
        }

        .map-panes-right {
          width: 30rem;
          display: flex;
          flex-direction: column;
          margin-right: 3.6rem;
          margin-bottom: 1.6rem;
        }

        .map-pane-cart {
          max-height: calc(100% - 35rem);
        }

        .back-to-results {
          border-top: solid 0.1rem var(--secondaryColorFaded85);
          background-color: var(--secondaryColorFaded95);

          padding: 0.6rem 1.2rem;
          font-size: 1.2rem;
          display: flex;
          align-items: center;
          font-weight: 600;
          cursor: pointer;
        }

        .result-type-buttons {
          margin-top: 1rem;
          margin-bottom: -0.8rem;
          background: none;
          box-shadow: none;

          .btn-default {
            border-color: transparent;
          }

          .btn {
            box-shadow: none;
            border-radius: 0.2rem 0.2rem 0 0;
            border-left: solid 0.1rem rgba(0, 0, 0, 0.15);
            font-size: 1.1rem;

            svg {
              margin-right: 0;
            }

            .title {
              display: none;
            }
          }

          .btn.selected {
            border-left: solid 0rem rgba(0, 0, 0, 0.15);

            color: var(--secondaryColorReverse);
            background-color: var(--secondaryColor);

            svg {
              margin-right: 0.6rem;

              fill: var(--secondaryColorReverse);
              stroke: var(--secondaryColorReverse);
            }

            .title {
              display: inline;
            }
          }
        }

        .cart-section-header {
          background: var(--tableHeaderBackgroundColor);
          color: var(--tableHeaderForegroundColor);

          padding: 0.8rem 1.6rem;
          border-top: solid 0.1rem rgba(0, 0, 0, 0.1);

          .circle-btn {
            position: fixed;
            z-index: 1;
            width: 3.7rem;
            height: 3.7rem;
            margin-top: -3.7rem;
            /*margin-left: 22.8rem;*/
          }
        }

        .street-view-warning {
          color: rgba(0, 0, 0, 0.5);
          background-color: color.adjust(#fff, $alpha: 0.5) !important;
          position: absolute;
          bottom: 0.8rem;
          left: 50%;
          width: 18rem;
          padding: 0.3rem 0.6rem;
          border-radius: 1.6rem;
          margin: auto auto auto -9rem;
          text-align: center;

          span {
            text-shadow: 0 0 0.5rem #fff;
          }
        }
      }

      .filters {
        pointer-events: all;
        justify-content: flex-start;
        display: flex;
        align-items: center;
        flex-wrap: wrap;

        .filter {
          display: inline-block;
          position: relative;
          box-shadow: 0 0.3rem 0 rgba(0, 0, 0, 0.15);
          margin-right: 0.8rem;
          margin-bottom: 0.6rem;

          svg {
            fill: #ffffff;
            stroke: #ffffff;
            position: relative;
            top: 0.4rem;
            width: 1.2rem;
          }

          .label {
            top: 0.2rem;
            position: relative;
          }

          .close {
            margin-left: 0.6rem;
            margin-bottom: 0.3rem;
            color: #fff;
          }
        }
      }

      .full-screen {
        /*opacity: 0.2;*/
        /*z-index: 2;*/
        pointer-events: all;
      }

      .main-actions-menu {
        position: absolute;
        left: 32rem;
        width: 25rem;
        top: 1.6rem;
        background: #fff;
        border: solid 0.1rem $borders;
        box-shadow: $shadow;
        z-index: 3;

        ul {
          padding: 0;
          margin: 0;

          li {
            padding: 0.8rem 1.2rem;
            list-style: none;
            cursor: pointer;
            display: flex;
            align-items: center;

            color: var(--actionsColor);

            .icon {
              display: flex;
              align-items: center;

              svg {
                width: 1.6rem;
                height: 1.6rem;

                stroke: var(--actionsColor);
                fill: var(--actionsColor);
              }

              margin-right: 0.8rem;
            }

            &:hover {
              background: var(--actionsColor);
              color: var(--actionsColorReverse);

              .icon {
                svg {
                  stroke: var(--actionsColorReverse);
                  fill: var(--actionsColorReverse);
                }
              }
            }
          }
        }
      }

      .cpm-info {
        display: flex;
        align-items: flex-start;

        .kpi {
          flex-basis: auto;
        }
      }

      .cpm-histogram {
        height: 2.4rem;
        display: flex;
        margin: 0.8rem 0;
        flex-grow: 1;

        .cpm-histogram-bar {
          margin: 0 1%;
          flex-grow: 1;
          position: relative;
          background: #eee;

          .bar {
            width: 100%;
            position: absolute;
            bottom: 0;
            min-height: 0.1rem;

            background-color: var(--primaryColor);
          }
        }
      }

      .search-box-container {
        display: flex;
        position: relative;

        .actions {
          padding: 0 0.8rem;
          border-radius: 0;
        }
      }

      .search-box {
        flex-grow: 1;
        display: flex;
        flex-direction: row;

        input {
          flex-grow: 1;
          height: 3.6rem;
          outline: none;
          padding: 0.6rem 1.2rem;
          border: none;
          background: rgba(0, 0, 0, 0.05);
        }

        .btn {
          stroke: var(--textColor);
          fill: var(--textColor);

          border: none !important;
          box-shadow: none !important;
          margin: 0 0.6rem;
        }

        .separator {
          background: $borders-light;
          width: 0.1rem;
        }

        .loader-container {
          margin: auto 0.6rem;
        }
      }

      .loading {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        display: flex;
        align-items: center;
        justify-content: center;

        > div {
          background: $borders-light;
          border-radius: 3.2rem;
          padding: 0.8rem 1.6rem;
          border: solid 0.1rem $borders;
          box-shadow: $small-shadow;
          display: flex;
          align-items: center;

          color: var(--textMutedColor);

          span {
            margin-left: 1rem;
          }
        }
      }
    }

    .drop-down-filters-container {
      position: absolute;
      z-index: 1;
      padding: 0.8rem;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      margin-right: 4rem;
      opacity: 0;
      transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
      pointer-events: none;

      > * {
        margin-right: 0.6rem;
        margin-top: 0.6rem;
        margin-bottom: 0.6rem;
        pointer-events: initial;

        &:last-of-type {
          margin-right: 0;
        }
      }

      &.visible {
        opacity: 1;
        transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
      }

      .radius-distance-control {
        margin: 0 1rem;
      }

      .slider {
        margin: 0 1rem;

        .vue-slider {
          margin: 0;
        }
      }
    }

    .message-select-targeting {
      position: absolute;
      z-index: 1;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      background: #fff;
      padding: 2rem;
      box-shadow:
        0 1rem 1.5rem -0.3rem rgb(0 0 0 / 0.1),
        0 0.4rem 0.6rem -0.4rem rgb(0 0 0 / 0.1);
      border: 0.1rem solid var(--defaultButtonBorderColor);
      color: var(--defaultButtonColor);
      border-radius: 0.4rem;
    }

    .circle-btn {
      &.off {
        background: #fff;

        svg {
          stroke: var(--textMutedColor) !important;
          fill: var(--textMutedColor) !important;
        }
      }

      &.on {
        background: var(--secondaryColor);

        svg {
          stroke: var(--secondaryColorReverse) !important;
          fill: var(--secondaryColorReverse) !important;
        }
      }
    }

    .active-filters-pane,
    .search-results-pane {
      transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
      margin-left: -35rem;
      width: 33.3rem;
      height: 0;

      &.visible {
        transition: all 0.5s cubic-bezier(0.2, 1, 0.2, 1);
        margin-left: 0;
        height: auto;
      }
    }

    .search-results {
      /*position: fixed;
    z-index: 3;
    background: #fff;
    border: solid 0.1rem $borders;
    width: 33rem;
    box-shadow: $shadow;
    top: 22rem;
    margin-left: 1.6rem;*/

      .search-result-header {
        border-top: solid 2px $borders;
        background: $borders-light;

        padding: 1.2rem 1.6rem;
        font-weight: 900;
        display: flex;
        align-items: center;

        svg {
          width: 1.6rem;
          height: 1.6rem;
          margin-right: 0.6rem;

          fill: var(--textColor) !important;
          stroke: var(--textColor) !important;
        }
      }

      .list-item {
        font-size: 0.9em;
      }

      ul {
        overflow: visible;
        max-height: none !important;
      }
    }

    ul {
      padding: 0;
      margin-bottom: 0;

      &.has-images {
        .list-item {
          .icon {
            display: flex;
            align-items: center;
            padding: 1.8rem 2.8rem;
            margin: 0;
            border-right: solid 0.1rem rgba(0, 0, 0, 0.025);
            background: rgba(0, 0, 0, 0.05);
          }

          .info-and-actions {
            .info {
              max-width: 20.8rem;
            }
          }

          &:hover {
            .info-and-actions {
              .info {
                max-width: 18.4rem;
              }
            }
          }
        }
      }

      .list-item {
        display: flex;
        align-items: center;
        border-top: solid 0.1rem rgba(0, 0, 0, 0.05);
        cursor: pointer;

        &:hover {
          color: var(--actionsColor);
          background-color: var(--tactionsColorFaded90) !important;

          .info-and-actions {
            .actions {
              display: flex;
            }

            .info {
              max-width: 22rem;
            }
          }
        }

        .icon {
          margin-left: 2rem;

          svg {
            height: 2.4rem;
            width: 2.4rem;
          }
        }

        .info-and-actions {
          padding: 1.2rem 1.6rem;
          align-items: center;
          flex-grow: 1;
          display: flex;
        }

        .info {
          flex-grow: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 24.4rem;
          margin-right: 0.8rem;
        }

        .actions {
          display: none;
          flex-basis: auto;
        }

        img {
          max-height: 6rem;
        }
      }
    }

    //
    // Proximity Loader
    //
    div.proximity-loader-container {
      display: flex;
      flex-direction: column;
      align-items: center;

      div.proximity-loader {
        display: flex;
        width: 3.5em;
        height: 3.5em;
        border: 3px solid transparent;

        border-top-color: var(--primaryColor);
        border-bottom-color: var(--primaryColor);

        border-radius: 50%;
        animation: proximity-loader-spin 1.5s linear infinite;
      }

      div.proximity-loader:before {
        content: '';
        display: block;
        margin: auto;
        width: 0.75em;
        height: 0.75em;

        border: 3px solid var(--primaryColor);

        border-radius: 50%;
        animation: proximity-loader-pulse 1s alternate ease-in-out infinite;
      }

      @keyframes proximity-loader-spin {
        to {
          transform: rotate(360deg);
        }
      }

      @keyframes proximity-loader-pulse {
        from {
          transform: scale(0.5);
        }

        to {
          transform: scale(1);
        }
      }
    }

    .missing-ids-list {
      padding: 1rem;

      ul {
        padding-left: 2rem;
      }
    }

    .combine-search-result-container {
      font-size: smaller;
      margin-left: 1rem;
      margin-bottom: 1rem;

      input {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
        margin-right: 1rem;
      }
    }

    .network-colors-legend {
      position: absolute;
      left: 1.6rem;
      bottom: 1.6rem;
      background: rgba(255, 255, 255, 0.8);
      padding: 0.8rem 1.2rem;
      box-shadow: var(--panesShadow);
      border-radius: 0.4rem;
      pointer-events: initial;
      max-height: 50%;
      display: flex;
      flex-direction: column;

      .header {
        flex-shrink: 0;
        font-weight: 600;
        font-size: 0.9em;
        border-bottom: solid 0.1rem $borders-light;
        margin-bottom: 0.6rem;
        padding-bottom: 0.3rem;
      }

      .list {
        overflow: auto;
        flex-grow: 1;
      }

      td {
        font-size: 0.8em;

        div {
          width: 1.4rem;
          height: 1.4rem;
          margin-right: 0.6rem;
        }

        span {
          display: block;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 15rem;
        }
      }
    }
  }
</style>
